import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAetherAuth } from './AetherAuthContext';
import AetherUserManagement from './AetherUserManagement';
import InvoiceBuilder from './InvoiceBuilder';
import AdminListView from './AdminListView';
import RequestsView from './RequestsView';
import AffiliateDetail from './AffiliateDetail';
import DashboardHome from './DashboardHome';
import FloatingHeader from '../../components/layout/FloatingHeader';
import { SkeletonCard } from '../../components/Skeleton';
import { openInvoice } from './invoice';
import { 
  Sparkles,
  Loader2,
  Zap, 
  Download,
  History,
  Box,
  Send,
  Compass,
  Shapes,
  Palette,
  Trash2,
  Layout,
  Type,
  X,
  Maximize2,
  Smile,
  Layers,
  Waves,
  Sun,
  Cpu,
  Copy,
  Check,
  FileDown,
  RefreshCw,
  Search,
  Eye,
  ImagePlus,
  Target,
  RotateCcw,
  Plus,
  Home,
  Wind,
  Lightbulb,
  BookOpen,
  User,
  Activity,
  ChevronRight,
  BoxSelect,
  PenLine,
  Book,
  Clock,
  Calendar,
  Menu,
  Archive,
  Settings,
  ShoppingBag,
  Lock,
  ArrowLeft,
  Mic,
  // Admin Icons
  Users,
  Tag,
  TrendingUp,
  DollarSign,
  Percent,
  Link as LinkIcon,
  Mail,
  Key,
  Package,
  LogOut,
  ListTodo,
  Shield,
  Download as DownloadIcon,
  Rocket,
  CheckCircle2,
  AlertCircle,
  Gift,
  Inbox,
  Boxes,
  MessagesSquare,
  UserCog
} from 'lucide-react';

// --- STILE (Premium Glass-Mode & Responsive Layout) ---
// Fonts loaded globally via @fontsource in src/index.js — no Google request.
const glassStyles = `

  .aether-container {
    background: transparent;
    color: white;
    font-family: 'Inter', sans-serif;
    overflow: hidden;
  }

  .aether-container .font-serif { font-family: 'Playfair Display', serif; }
  .aether-container .font-mono { font-family: 'Space Mono', monospace; }

  .monolith-glass {
    position: relative;
    background: linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%);
    backdrop-filter: blur(20px) saturate(140%);
    box-shadow: 0 8px 32px 0 rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.08);
    border-radius: 1.5rem;
    transition: all 400ms cubic-bezier(0.23, 1, 0.32, 1);
  }

  .monolith-heavy {
    position: relative;
    background: rgba(10, 10, 10, 0.85);
    backdrop-filter: blur(40px) saturate(120%);
    box-shadow: 0 12px 40px 0 rgba(0,0,0,0.9), inset 0 0 0 1px rgba(255,255,255,0.05);
    border-radius: 1.5rem;
    transition: all 400ms cubic-bezier(0.23, 1, 0.32, 1);
  }

  .monolith-glass::before, .monolith-heavy::before {
    content: "";
    position: absolute;
    inset: 0;
    padding: 1px;
    border-radius: inherit;
    background: linear-gradient(to bottom, rgba(255,255,255,0.2), transparent, rgba(255,255,255,0.02));
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: destination-out;
    mask-composite: exclude;
    pointer-events: none;
    z-index: 1;
  }

  .aether-grain-overlay {
    position: fixed;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
    background-size: 100px 100px;
    opacity: 0.06;
    pointer-events: none;
    z-index: 5;
  }

  .aether-scrollbar::-webkit-scrollbar { width: 4px !important; height: 4px !important; }
  .aether-scrollbar::-webkit-scrollbar-track { background: rgba(0, 0, 0, 0.2) !important; }
  .aether-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.15) !important; border-radius: 10px !important; }
  .aether-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.25) !important; }
  .aether-scrollbar { scrollbar-width: thin !important; scrollbar-color: rgba(255, 255, 255, 0.15) rgba(0, 0, 0, 0.2) !important; }

  /* Ensure ALL scrollbars within aether-container use dark theme */
  .aether-container ::-webkit-scrollbar { width: 4px !important; height: 4px !important; }
  .aether-container ::-webkit-scrollbar-track { background: rgba(0, 0, 0, 0.2) !important; }
  .aether-container ::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.15) !important; border-radius: 10px !important; }
  .aether-container ::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.25) !important; }
  .aether-container * { scrollbar-width: thin !important; scrollbar-color: rgba(255, 255, 255, 0.15) rgba(0, 0, 0, 0.2) !important; }

  .nav-btn { color: rgba(255,255,255,0.4); transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1); }
  .nav-btn.active { color: white; background: rgba(255,255,255,0.05); }
  .nav-btn:hover { transform: translateX(4px); }

  /* Scriptum target switch (Journal | Tasks | Beides) collapses to
     icon-only when the right sidebar is narrowed past ~220px. */
  .scriptum-target-bar { container-type: inline-size; }
  @container (max-width: 220px) {
    .scriptum-target-label { display: none; }
  }

  /* Task Feed header — icon + count remain, "Task Feed" + " offen"
     suffix drop out when the sidebar is narrowed. */
  .task-feed-header { container-type: inline-size; }
  @container (max-width: 240px) {
    .task-feed-label, .task-feed-suffix { display: none; }
  }

  /* Hover animations for cards/boxes */
  .aether-card-hover {
    transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  }
  .aether-card-hover:hover {
    transform: scale(1.02) translateY(-2px);
    box-shadow: 0 20px 60px 0 rgba(0,0,0,0.8), inset 0 0 0 1px rgba(255,255,255,0.12);
  }

  /* Spotlight effect container */
  .aether-spotlight {
    position: relative;
    overflow: hidden;
  }
  .aether-spotlight::after {
    content: "";
    position: absolute;
    inset: 0;
    opacity: 0;
    transition: opacity 0.5s ease;
    pointer-events: none;
    z-index: 1;
  }
  .aether-spotlight:hover::after {
    opacity: 1;
  }

  /* Button hover glow */
  .aether-btn-glow {
    position: relative;
    overflow: hidden;
  }
  .aether-btn-glow::before {
    content: "";
    position: absolute;
    inset: -2px;
    background: linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }
  .aether-btn-glow:hover::before {
    transform: translateX(100%);
  }

  /* Simple Timeline - Clean & Minimal */
  .timeline-line {
    position: absolute;
    left: 10px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgba(255, 255, 255, 0.15);
  }
  .timeline-dot {
    width: 10px;
    height: 10px;
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
  }

  .mobile-drawer {
    position: fixed;
    top: 0;
    bottom: 0;
    width: 80%;
    max-width: 300px;
    z-index: 150;
    transition: transform 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  }

  .mobile-drawer.right-0 {
    width: 75%;
    max-width: 280px;
  }

  /* Tablet (iPad) - hide sidebars like mobile */
  @media (min-width: 768px) and (max-width: 1023px) {
    .mobile-drawer {
      position: fixed;
      width: 320px;
      max-width: 320px;
    }
    .mobile-drawer.right-0 {
      width: 300px;
      max-width: 300px;
    }
  }

  /* Desktop - show sidebars inline. Widths come from CSS variables that
     React updates as the user drags. On mobile the variables are ignored
     and the drawer behaviour (position:fixed 80% width) takes over. */
  @media (min-width: 1024px) {
    .mobile-drawer {
      position: static;
      transform: none !important;
      z-index: 1;
      height: 100%;
      flex-shrink: 0;
      width: var(--aether-left-width, 288px);
      min-width: var(--aether-left-width, 288px);
      max-width: var(--aether-left-width, 288px);
    }
    .mobile-drawer.right-0 {
      position: relative;
      flex-shrink: 0;
      width: var(--aether-right-width, 320px);
      min-width: var(--aether-right-width, 320px);
      max-width: var(--aether-right-width, 320px);
    }
  }

  .floating-export {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 200;
  }

  @media (max-width: 1024px) {
    .floating-export {
      bottom: 80px;
    }
  }
`;

// --- API KONFIGURATION ---
const API_URL = process.env.REACT_APP_BACKEND_URL;

// API Keys are now handled by the backend proxy - no need to expose them in frontend
// Use backend endpoints: /api/aether/chat, /api/aether/generate-text, /api/aether/generate-image

// Model selection (for display purposes)
const GEMINI_TEXT_MODEL = "gemini-2.0-flash";
const GEMINI_IMAGE_MODEL = "imagen-3.0-generate-002";
const OPENAI_TEXT_MODEL = "gpt-4o";
const OPENAI_IMAGE_MODEL = "dall-e-3";

// Helper function to call backend text generation proxy
const generateTextViaBackend = async (prompt, systemPrompt = null, model = 'openai', maxTokens = 500, jsonResponse = false) => {
  const res = await fetch(`${API_URL}/api/aether/generate-text`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      prompt,
      system_prompt: systemPrompt,
      model,
      max_tokens: maxTokens,
      json_response: jsonResponse
    })
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.detail || 'Text generation failed');
  }
  return res.json();
};

// Helper function to call backend image generation proxy
const generateImageViaBackend = async (prompt, model = 'openai', aspectRatio = '1:1') => {
  const res = await fetch(`${API_URL}/api/aether/generate-image`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      prompt,
      model,
      aspect_ratio: aspectRatio
    })
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.detail || 'Image generation failed');
  }
  return res.json();
};

// Canonical list of the LIVE, sellable plugins — the single source of truth for
// every forward-looking product picker (order filter, license grant, broadcast).
// SFX Grid (removed from shop) and Effect Flow (coming-soon, no build yet) are
// deliberately NOT here; add Effect Flow to this list once it ships.
const PLUGIN_PRODUCTS = [
  // Nur EIN Abo-Eintrag: verschenkt wird ein Schluessel, der alles oeffnet —
  // wie lange, sagt die Laufzeit daneben. Zwei Eintraege "Monat"/"Jahr" taeten
  // beim Verschenken exakt dasselbe und wuerden nur Fragen aufwerfen.
  { id: 'abo-jahr', name: 'All-in-One (alles)' },
  { id: 'studio-flow', name: 'Studio Flow' },
  { id: 'media-flow', name: 'Media Flow' },
  { id: 'instant-grade', name: 'Instant Grade' },
  { id: 'instant-curve', name: 'Instant Curve' },
  { id: 'bounce-flow', name: 'Bounce Flow' },
  { id: 'shake-flow', name: 'Shake Flow' },
  { id: 'glitch-flow', name: 'Glitch Flow' },
  { id: 'text-flow', name: 'Text Flow' },
  { id: 'grid-flow', name: 'Grid Flow' },
  { id: 'carousel-flow', name: 'Carousel Flow' },
  { id: 'shape-flow', name: 'Shape Flow' },
  { id: 'background-flow', name: 'Background Flow' },
  { id: 'instant-ui-kit', name: 'Instant UI Kit' },
  { id: 'instant-comp', name: 'Instant Comp' },
];

// Order-filter dropdown: "Alle Produkte" + the live plugins.
const ADMIN_PRODUCTS = [{ id: '', name: 'Alle Produkte' }, ...PLUGIN_PRODUCTS];

const DISCOUNT_SCOPES = [
  { id: 'all_products', name: 'Alle Produkte' },
  { id: 'cart_total', name: 'Gesamter Warenkorb' },
  { id: 'specific_product', name: 'Bestimmtes Produkt' }
];

const PRESETS = [
  { id: 'p1', name: 'Stone Silence', seed: 'Brutalistischer Stein, monumentale Stille', style: 'Raw brutalist stone textures, monolithic structure, soft morning light', icon: Box, img: 'https://images.pexels.com/photos/6120151/pexels-photo-6120151.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { id: 'p2', name: 'Cyber Glass', seed: 'Kybernetisches Glas, digitale Tiefe', style: 'Complex layered frosted glass panels, obsidian core, tech minimalism', icon: Layers, img: 'https://images.pexels.com/photos/3399679/pexels-photo-3399679.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { id: 'p3', name: 'Typographic Void', seed: 'Abstrakte Typografie, Kontrast', style: 'High-contrast typography art, large serif letters integrated into geometry', icon: Type, img: 'https://images.pexels.com/photos/6480785/pexels-photo-6480785.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { id: 'p4', name: 'Fluid Obsidian', seed: 'Flüssiges Obsidian, organische Materie', style: 'Flowing organic liquid obsidian, glossy reflections, dark void background', icon: Waves, img: 'https://images.pexels.com/photos/3960244/pexels-photo-3960244.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { id: 'p5', name: 'Ethereal Aura', seed: 'Ätherisches Licht, Nebeleffekte', style: 'Soft glowing ethereal forms, thick fog, volumetric white light', icon: Sun, img: 'https://images.unsplash.com/photo-1772700260494-c627971ec504?auto=format&fit=crop&q=80&w=600' },
  { id: 'p6', name: 'Industrial Grit', seed: 'Gebürsteter Stahl, technischer Purismus', style: 'Brushed dark steel surface, precise industrial rills, cold lighting', icon: Cpu, img: 'https://images.pexels.com/photos/36095234/pexels-photo-36095234.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { id: 'p7', name: 'Carbon Shadow', seed: 'Mattschwarzer Kohlenstoff, Texturen', style: 'Matte black carbon fiber textures, deep shadows, geometric precision, stealth look', icon: Palette, img: 'https://images.pexels.com/photos/596815/pexels-photo-596815.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { id: 'p8', name: 'Minimal Horizon', seed: 'Minimalistischer Horizont, Weite', style: 'Ultramodern minimal architectural horizon, vast space, clean division of light and dark', icon: Sun, img: 'https://images.unsplash.com/photo-1767358270258-efc76644440a?auto=format&fit=crop&q=80&w=600' },
  { id: 'p9', name: 'Kinetic Light', seed: 'Kinetische Energie, Lichtspuren', style: 'Abstract long exposure light trails, monochrome motion blur, dynamic energy lines', icon: Zap, img: 'https://images.pexels.com/photos/7293754/pexels-photo-7293754.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { id: 'p10', name: 'Organic Flow', seed: 'Organischer Fluss, Naturmuster', style: 'Macro abstract of natural ripples, monochrome sand dunes, fluid organic shapes', icon: Waves, img: 'https://images.pexels.com/photos/5648796/pexels-photo-5648796.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { id: 'p11', name: 'Chrome Distortion', seed: 'Chromatische Verzerrung, Metall', style: 'Polished liquid metal, mercury-like reflections, abstract metallic distortion', icon: Cpu, img: 'https://images.unsplash.com/photo-1739056238852-9ebc2d3f9407?auto=format&fit=crop&q=80&w=600' },
  { id: 'p12', name: 'Geometric Void', seed: 'Geometrische Leere, Abgrund', style: 'Deep black geometric pit, recursive squares fading into darkness, void aesthetic', icon: Shapes, img: 'https://images.unsplash.com/photo-1749899843885-d4af2a7f9236?auto=format&fit=crop&q=80&w=600' },
  { id: 'p13', name: 'Brutalist Peak', seed: 'Sichtbeton Architektur, Brutalismus', style: 'Monumental raw concrete architecture, grayscale', icon: Home, img: 'https://images.pexels.com/photos/4015825/pexels-photo-4015825.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { id: 'p14', name: 'Glass Pavilion', seed: 'Minimalistischer Glaspavillon', style: 'Transparent glass house structure, steel frames', icon: Layers, img: 'https://images.unsplash.com/photo-1552835376-89b8cdfacb4d?auto=format&fit=crop&q=80&w=600' },
  { id: 'p15', name: 'Zen Sanctuary', seed: 'Japanischer Minimalismus, Stein & Licht', style: 'Japanese rock garden, soft light', icon: Wind, img: 'https://images.pexels.com/photos/327483/pexels-photo-327483.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { id: 'p16', name: 'Bauhaus Grid', seed: 'Bauhaus Design, geometrische Struktur', style: 'Bauhaus building facade, grid system, high contrast', icon: Layout, img: 'https://images.pexels.com/photos/1029611/pexels-photo-1029611.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { id: 'p17', name: 'Marble Echo', seed: 'Carrara Marmor, fließende Adern', style: 'Smooth white marble surface', icon: Palette, img: 'https://images.unsplash.com/photo-1566041510394-cf7c8fe21800?auto=format&fit=crop&q=80&w=600' },
  { id: 'p18', name: 'Desert Monolith', seed: 'Wüstenmonolith, einsame Form', style: 'Geometric object in vast desert', icon: Box, img: 'https://images.unsplash.com/photo-1645460273611-6eeed8e34e02?auto=format&fit=crop&q=80&w=600' },
  { id: 'p19', name: 'Ethereal Loft', seed: 'Lichtdurchflutetes Loft, Weißraum', style: 'Minimal loft architecture, soft natural light', icon: Home, img: 'https://images.pexels.com/photos/6045331/pexels-photo-6045331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { id: 'p20', name: 'Obsidian Monolith', seed: 'Polierter Obsidian, schwarzer Kubus', style: 'Black polished obsidian monolithic structure', icon: Box, img: 'https://images.unsplash.com/photo-1582035100994-9ddfc34b1dae?auto=format&fit=crop&q=80&w=600' },
  { id: 'p21', name: 'Nordic Shell', seed: 'Nordische Architektur, Holz & Beton', style: 'Scandinavian minimal architecture', icon: Home, img: 'https://images.pexels.com/photos/7542670/pexels-photo-7542670.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { id: 'p22', name: 'Digital Dust', seed: 'Digitaler Zerfall, Partikelwolken', style: 'Monochrome abstract particles', icon: Zap, img: 'https://images.pexels.com/photos/2020309/pexels-photo-2020309.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { id: 'p23', name: 'Concrete Reflection', seed: 'Betonwand, Wasserreflektion', style: 'Brutalist concrete reflecting in water', icon: Waves, img: 'https://images.unsplash.com/photo-1768621892401-520bc4890098?auto=format&fit=crop&q=80&w=600' },
  { id: 'p24', name: 'Void Sculpture', seed: 'Skulptur im leeren Raum', style: 'Geometric marble sculpture', icon: BoxSelect, img: 'https://images.unsplash.com/photo-1752614060011-500036a39124?auto=format&fit=crop&q=80&w=600' },
  // Pixar / Cartoon Illustration Series — perfect for YouTube thumbnails & content
  { id: 'p25', name: 'Cozy Workspace', seed: 'Warmes Lampenlicht, gemütliche Atmosphäre', style: 'Pixar-style 2D cartoon illustration, painterly digital art, warm desk-lamp lighting in a dim room, deep golden tones with soft blue shadows, smooth painterly textures, friendly stylized character design with simple round faces, cinematic depth of field, atmospheric and intimate mood', palette: 'color', icon: Lightbulb, img: '/portfolio/p25.jpg' },
  { id: 'p26', name: 'Bright Studio', seed: 'Helle Tageslicht-Illustration, pastellige Farben', style: 'Pixar-style 2D cartoon illustration, painterly digital art, bright airy daylight, soft pastel color palette with peach and teal accents, large windows with natural light, smooth painterly textures, friendly stylized character design, clean modern composition, optimistic and welcoming mood', palette: 'color', icon: Palette, img: '/portfolio/p26.jpg' },
  { id: 'p27', name: 'Friendly Tech', seed: 'Freundliche Tech-Atmosphäre, weiches Blau', style: 'Pixar-style 2D cartoon illustration, painterly digital art, soft cool-blue tech lighting with warm highlights, modern minimal interior, smooth painterly textures, friendly stylized character design with expressive eyes, clean modern composition, hopeful and approachable tech mood', palette: 'color', icon: Smile, img: '/portfolio/p27.jpg' },
  { id: 'p28', name: 'Sci-Fi Lab', seed: 'Futuristisches Lab, kühles Tech-Licht', style: 'Pixar-style 2D cartoon illustration, painterly digital art, cool blue and cyan ambient lighting with glowing accents, futuristic minimal lab interior, smooth painterly textures, stylized friendly sci-fi character design, atmospheric haze, cinematic depth of field, polished tech aesthetic', palette: 'color', icon: Cpu, img: '/portfolio/p28.jpg' },
  { id: 'p29', name: 'Golden Hour', seed: 'Goldene Stunde, warmer Lampenschein', style: 'Pixar-style 2D cartoon illustration, painterly digital art, warm golden-hour interior lighting, deep amber and orange tones with soft brown shadows, smooth painterly textures, friendly stylized character design, cinematic depth of field, cozy nostalgic mood', palette: 'color', icon: BookOpen, img: '/portfolio/p29.jpg' }
];

async function fetchWithRetry(url, payload, retries = 3) {
  let lastError = null;
  for (let i = 0; i < retries; i++) {
    try {
      const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      
      if (response.ok) {
        try {
          return await response.json();
        } catch (jsonErr) {
          throw new Error('Ungültige Antwort vom Server');
        }
      }
      
      // Check for specific error codes
      if (response.status === 429) {
        throw new Error('API-Limit erreicht. Bitte versuche es später erneut oder wechsle das KI-Modell (z.B. von Gemini zu GPT).');
      }
      if (response.status === 401 || response.status === 403) {
        throw new Error('API-Schlüssel ungültig oder abgelaufen. Bitte überprüfe die Konfiguration.');
      }
      if (response.status >= 500) {
        throw new Error(`Server-Fehler (${response.status}). Bitte versuche es später erneut.`);
      }
      
      // Generic error for other status codes
      lastError = new Error(`API-Fehler (${response.status}). Bitte versuche ein anderes KI-Modell.`);
    } catch (e) { 
      // Catch clone errors from external scripts
      if (e.message && e.message.includes('clone')) {
        // This is likely a quota error that external recorder interfered with
        throw new Error('API-Limit erreicht. Bitte wechsle das KI-Modell (z.B. von Gemini zu GPT).');
      }
      lastError = e;
      if (e.message.includes('429') || e.message.includes('API-Limit') || e.message.includes('API-Schlüssel')) {
        throw e; // Don't retry quota/auth errors
      }
      if (i === retries - 1) throw e;
    }
    await new Promise(r => setTimeout(r, Math.pow(2, i) * 1000));
  }
  throw lastError || new Error('Verbindung fehlgeschlagen');
}

function extractJSON(text) {
  try {
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (jsonMatch) return JSON.parse(jsonMatch[0]);
    return JSON.parse(text);
  } catch (e) {
    return { manifesto: "Fragment.", analysis: "Fehler.", tags: ["VOID"] };
  }
}

function safeRender(val) {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (Array.isArray(val)) return val.map(v => typeof v === 'object' ? JSON.stringify(v) : String(v)).join(', ');
  if (typeof val === 'object') {
    if (val.$$typeof) return ''; 
    const res = val.name || val.value || JSON.stringify(val);
    return typeof res === 'object' ? JSON.stringify(res) : String(res);
  }
  return String(val);
}

// Animation variants for Framer Motion
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.4 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const staggerItem = {
  initial: { opacity: 0, y: 15 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
};

// Dark mode select styling
const darkSelectStyle = {
  colorScheme: 'dark',
  backgroundColor: '#0a0a0a'
};

const darkOptionStyle = { 
  backgroundColor: '#0a0a0a', 
  color: '#ffffff' 
};

export default function AetherDashboard() {
  const navigate = useNavigate();
  const { user: aetherUser, isAdmin, isPartner, isCustomer, role: userRole, logout: aetherLogout, authFetch } = useAetherAuth();
  // Default landing tab depends on role: customer/partner see the new
  // overview Dashboard; admins keep landing on Labor (their primary
  // workspace) to match existing muscle memory.
  // Als Admin landete man frueher auf '01' — dem Synthese-Labor. Das ist aus
  // der Seitenleiste raus, also darf es auch nicht mehr die Startansicht sein.
  // Alle beginnen jetzt auf dem Dashboard.
  const [activeNav, setActiveNav] = useState('dashboard');
  const [showMobileVault, setShowMobileVault] = useState(false);

  // Resizable sidebars — width persisted in localStorage so the user's
  // preferred layout sticks across sessions. Clamped to [180, 480] so the
  // sidebar can't be dragged to zero (unrecoverable) or eat the whole screen.
  const [leftSidebarWidth, setLeftSidebarWidth] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('aether_left_width'), 10);
      return Number.isFinite(v) ? Math.max(180, Math.min(480, v)) : 288;
    } catch { return 288; }
  });
  const [rightSidebarWidth, setRightSidebarWidth] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('aether_right_width'), 10);
      return Number.isFinite(v) ? Math.max(180, Math.min(480, v)) : 320;
    } catch { return 320; }
  });

  // Generic drag-resize handler. `side` is 'left' | 'right' — for the right
  // sidebar the drag goes the OTHER way (dragging left grows it).
  const startSidebarResize = (side) => (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = side === 'left' ? leftSidebarWidth : rightSidebarWidth;
    let current = startWidth;
    const setter = side === 'left' ? setLeftSidebarWidth : setRightSidebarWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (me) => {
      const dx = me.clientX - startX;
      const next = side === 'left' ? startWidth + dx : startWidth - dx;
      current = Math.max(180, Math.min(480, next));
      setter(current);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try { localStorage.setItem(`aether_${side}_width`, String(current)); } catch {}
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);
  const [showPresetModal, setShowPresetModal] = useState(false);
  
  // Admin Auth State
  const [showAdminAuth, setShowAdminAuth] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(() => {
    // Check localStorage for persistent admin session
    return localStorage.getItem('aether_admin_authenticated') === 'true';
  });
  const [adminError, setAdminError] = useState('');
  
  // Tasks State (replacing Vault)
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  
  // Scriptum Post Options. Default depends on role:
  //   - admin   → 'journal' (their muscle memory; they have all three)
  //   - partner → 'tasks'   (no Journal button anymore — admin-only)
  //   - customer → 'tasks'  (target bar hidden entirely; only Tasks)
  const [scriptumTarget, setScriptumTarget] = useState(() =>
    isAdmin ? 'journal' : 'tasks'
  );
  const [showScriptumConfirm, setShowScriptumConfirm] = useState(false);
  const [pendingNote, setPendingNote] = useState('');
  
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [showNegativePrompt, setShowNegativePrompt] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(null);
  
  // History and Journal - loaded from API
  const [history, setHistory] = useState([]);
  const [journal, setJournal] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [styleSearchQuery, setStyleSearchQuery] = useState('');
  const [notes, setNotes] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState('');
  const [imageLoading, setImageLoading] = useState(false);
  const [extLoading, setExtLoading] = useState({ 
    emotion: false, vorhersage: false, distill: false, critique: false, 
    expansion: false, inspiration: false, soul: false, blueprint: false 
  });
  const [copied, setCopied] = useState(false);
  const [bundleLoading, setBundleLoading] = useState(false);
  
  // AI Model Selection: 'gemini' or 'openai'
  const [selectedModel, setSelectedModel] = useState('openai');
  
  // Image Format Selection
  const [imageFormat, setImageFormat] = useState('16:9');
  const imageFormats = [
    { id: '1:1', label: 'Quadrat', width: 1024, height: 1024 },
    { id: '16:9', label: 'Landscape', width: 1792, height: 1024 },
    { id: '9:16', label: 'Portrait', width: 1024, height: 1792 },
    { id: '4:3', label: '4:3', width: 1024, height: 768 },
    { id: '3:4', label: '3:4', width: 768, height: 1024 },
  ];
  
  // Generation Mode: 'both', 'text', 'image', 'chat'
  const [generationMode, setGenerationMode] = useState('chat');
  
  // Chat messages state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  
  // Settings dropdown
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  
  // Records filter: 'image', 'chat'
  const [recordsFilter, setRecordsFilter] = useState('chat');
  
  // Error state for API failures
  const [errorMessage, setErrorMessage] = useState(null);

  // ========== ADMIN STATES ==========
  const [adminStats, setAdminStats] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState(null);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [emailLog, setEmailLog] = useState([]);
  const [emailSearch, setEmailSearch] = useState('');
  const [emailStatusFilter, setEmailStatusFilter] = useState('all');

  // Activity log + Archive (audit/recovery system)
  const [activityLog, setActivityLog] = useState([]);
  const [activitySearch, setActivitySearch] = useState('');
  const [archiveItems, setArchiveItems] = useState([]);
  const [archiveKindFilter, setArchiveKindFilter] = useState('all');

  // Broadcast email composer
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({ subject: '', body_html: '', audience: 'all_paid' });
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [broadcastBusy, setBroadcastBusy] = useState(false);

  // 'Send all 6 templates as preview' state
  const [emailPreviewBusy, setEmailPreviewBusy] = useState(false);
  const [emailPreviewResult, setEmailPreviewResult] = useState(null);

  // GDPR tab — export / erase requests + retention sweeper
  const [gdprEmail, setGdprEmail] = useState('');
  const [gdprBusy, setGdprBusy] = useState(false);
  const [gdprResult, setGdprResult] = useState(null);
  const [retentionMonths, setRetentionMonths] = useState(12);
  const [retentionPreview, setRetentionPreview] = useState(null);
  const [adminCopiedCode, setAdminCopiedCode] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(null);
  const [resendSuccess, setResendSuccess] = useState(null);
  const [adminActiveTab, setAdminActiveTab] = useState('overview');
  // Der E-Mail-Bereich hat zwei Haelften, die nichts miteinander zu tun haben:
  // etwas verschicken und nachsehen, was verschickt wurde. Untereinander gestapelt
  // war das unuebersichtlich.
  const [mailTab, setMailTab] = useState('senden');
  const [customerTab, setCustomerTab] = useState('overview');

  // Update-Push form state — used by the 'updates' admin tab.
  const [updateForm, setUpdateForm] = useState({
    product_id: 'studio-flow',
    version: '',
    download_url: '',
    changelog: '',
  });
  const [updateSaving, setUpdateSaving]   = useState(false);
  const [updateNotifying, setUpdateNotifying] = useState(false);
  const [updateResult, setUpdateResult] = useState(null);
  // { kind: 'saved' | 'notified', ok, ...payload }
  
  // Admin Search states
  const [affiliateSearch, setAffiliateSearch] = useState('');
  const [affiliateActiveFilter, setAffiliateActiveFilter] = useState('all');
  const [discountSearch, setDiscountSearch] = useState('');
  const [discountActiveFilter, setDiscountActiveFilter] = useState('all');
  const [orderSearch, setOrderSearch] = useState('');
  
  // Admin Order filter states
  const [orderProductFilter, setOrderProductFilter] = useState('all');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderLicenseFilter, setOrderLicenseFilter] = useState('all');
  const [orderDateFilter, setOrderDateFilter] = useState('all');
  
  // Admin Form states
  const [newAffiliate, setNewAffiliate] = useState({ name: '', email: '' });
  const [newDiscount, setNewDiscount] = useState({ 
    code: '', 
    discount_percent: 10, 
    product_id: '', 
    max_uses: '',
    expires_at: '',
    scope: 'all_products'
  });

  // ========== LICENSE SYSTEM STATES ==========
  // License-token survives a page refresh just like the Aether JWT — same
  // security tier (admin scope), so dropping it forced an extra re-login
  // for no real safety win.
  const [licenseToken, setLicenseToken] = useState(() => localStorage.getItem('aether_license_token') || null);
  const [licenses, setLicenses] = useState([]);
  const [licenseStats, setLicenseStats] = useState(null);
  const [licenseProducts, setLicenseProducts] = useState([]);
  const [licenseSearch, setLicenseSearch] = useState('');
  const [licenseProductFilter, setLicenseProductFilter] = useState('all');
  const [licenseStatusFilter, setLicenseStatusFilter] = useState('all');
  const [licenseTypeFilter, setLicenseTypeFilter] = useState('all');
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [newLicense, setNewLicense] = useState({
    key_type: 'single',
    products: 'mediaflow',
    note: '',
    // null = perpetual key (every paid sale). Set to a positive int
    // to mint a trial key that auto-expires after that many days.
    expires_in_days: null,
  });

  // Admin grant — hand a product to any email for free (testers,
  // partners, comp friends). Mirrors a paid purchase from the
  // customer's perspective: PG license + transaction record + email.
  const [grantForm, setGrantForm] = useState({
    email: '',
    product_id: 'studio-flow',
    license_type: 'single',
    note: '', gueltig_tage: 365,
  });
  const [granting, setGranting] = useState(false);
  const [grantResult, setGrantResult] = useState(null); // { ok, license_key, error }
  // Existing Aether users — feeds the grant email picker so the admin
  // picks the EXACT account email (no typos, no case mismatch). Loaded
  // when the Lizenzen tab opens. Free-text entry still allowed for
  // testers who haven't registered yet.
  const [grantUsers, setGrantUsers] = useState([]);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [showLicenseDetail, setShowLicenseDetail] = useState(false);

  const [labState, setLabState] = useState({
    id: Date.now(), version: "1.0", manifesto: "", emotionMap: null, vorhersage: "", 
    distillation: "", visualCritique: "", expansion: "", soul: "", blueprint: "", tags: [], image: null, type: 'both'
  });

  const filteredHistory = history.filter(item => {
    const textMatch = safeRender(item.manifesto).toLowerCase().includes(searchQuery.toLowerCase());
    const searchMatch = textMatch || (Array.isArray(item.tags) && item.tags.some(t => safeRender(t).toLowerCase().includes(searchQuery.toLowerCase())));
    
    // Filter by type - only images and chats
    if (recordsFilter === 'image') return searchMatch && (item.type === 'image' || item.type === 'both') && item.image;
    if (recordsFilter === 'chat') return searchMatch && item.type === 'chat';
    return searchMatch;
  });

  const filteredPresets = PRESETS.filter(p => 
    p.name.toLowerCase().includes(styleSearchQuery.toLowerCase()) || 
    p.style.toLowerCase().includes(styleSearchQuery.toLowerCase())
  );

  // Admin Filtered Data
  const filteredAffiliates = affiliates.filter(a => 
    a.name?.toLowerCase().includes(affiliateSearch.toLowerCase()) ||
    a.email?.toLowerCase().includes(affiliateSearch.toLowerCase()) ||
    a.code?.toLowerCase().includes(affiliateSearch.toLowerCase())
  );

  const filteredDiscountCodes = discountCodes.filter(c =>
    c.code?.toLowerCase().includes(discountSearch.toLowerCase())
  );

  const filteredOrders = orders.filter(o => {
    const matchesSearch = !orderSearch || 
      o.order_number?.toLowerCase().includes(orderSearch.toLowerCase()) ||
      o.customer_email?.toLowerCase().includes(orderSearch.toLowerCase()) ||
      o.license_key?.toLowerCase().includes(orderSearch.toLowerCase()) ||
      o.product_name?.toLowerCase().includes(orderSearch.toLowerCase()) ||
      o.session_id?.toLowerCase().includes(orderSearch.toLowerCase()) ||
      o.discount_code?.toLowerCase().includes(orderSearch.toLowerCase()) ||
      o.affiliate_code?.toLowerCase().includes(orderSearch.toLowerCase());
    
    const matchesProduct = orderProductFilter === 'all' || o.product_id === orderProductFilter;
    const matchesStatus = orderStatusFilter === 'all' || o.payment_status === orderStatusFilter;
    const licenseType = o.metadata?.license_type || 'single';
    const matchesLicense = orderLicenseFilter === 'all' || licenseType === orderLicenseFilter;
    
    let matchesDate = true;
    if (orderDateFilter !== 'all' && o.created_at) {
      const orderDate = new Date(o.created_at);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      if (orderDateFilter === 'today') matchesDate = orderDate >= today;
      else if (orderDateFilter === 'week') matchesDate = orderDate >= weekAgo;
      else if (orderDateFilter === 'month') matchesDate = orderDate >= monthAgo;
    }
    
    return matchesSearch && matchesProduct && matchesStatus && matchesLicense && matchesDate;
  });

  const syncHistory = (updatedState) => {
    const recordWithTimestamp = { ...updatedState, timestamp: updatedState.timestamp || new Date().toLocaleTimeString() };
    
    setHistory(prev => {
      const exists = prev.find(item => item.id === updatedState.id);
      if (exists) {
        return prev.map(item => item.id === updatedState.id ? { ...recordWithTimestamp, timestamp: item.timestamp } : item);
      }
      return [recordWithTimestamp, ...prev].slice(0, 50);
    });
    
    // Save to API
    saveRecordToAPI(recordWithTimestamp);
  };

  // Load Records and Journal from API on mount
  useEffect(() => {
    const loadData = async () => {
      setDataLoading(true);
      try {
        const [recordsRes, journalRes] = await Promise.all([
          fetch(`${API_URL}/api/aether/records`),
          fetch(`${API_URL}/api/aether/journal`)
        ]);
        
        if (recordsRes.ok) {
          const records = await recordsRes.json();
          // Map server records to frontend format
          setHistory(records.map(r => ({
            id: r.record_id,
            manifesto: r.manifesto,
            emotionMap: r.emotionMap,
            vorhersage: r.vorhersage,
            distillation: r.distillation,
            visualCritique: r.visualCritique,
            expansion: r.expansion,
            soul: r.soul,
            blueprint: r.blueprint,
            tags: r.tags || [],
            image: r.image,
            type: r.type,
            timestamp: r.timestamp,
            version: r.version
          })));
        }
        
        if (journalRes.ok) {
          const entries = await journalRes.json();
          // Map server entries to frontend format
          setJournal(entries.map(e => ({
            id: e.entry_id,
            text: e.text,
            date: e.date,
            time: e.time
          })));
        }
      } catch (error) {
        console.error('Error loading Aether data:', error);
      } finally {
        setDataLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Save record to API
  const saveRecordToAPI = async (record) => {
    try {
      await fetch(`${API_URL}/api/aether/records`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          record_id: String(record.id),
          manifesto: record.manifesto || '',
          emotionMap: record.emotionMap || null,
          vorhersage: record.vorhersage || '',
          distillation: record.distillation || '',
          visualCritique: record.visualCritique || '',
          expansion: record.expansion || '',
          soul: record.soul || '',
          blueprint: record.blueprint || '',
          tags: record.tags || [],
          image: record.image || null,
          type: record.type || 'both',
          timestamp: record.timestamp || null,
          version: record.version || '1.0'
        })
      });
    } catch (error) {
      console.error('Error saving record:', error);
    }
  };

  // Delete record from API
  const deleteRecordFromAPI = async (recordId) => {
    try {
      await fetch(`${API_URL}/api/aether/records/${String(recordId)}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Error deleting record:', error);
    }
  };

  // Save journal entry to API
  const saveJournalToAPI = async (entry) => {
    try {
      await fetch(`${API_URL}/api/aether/journal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entry_id: String(entry.id),
          text: entry.text,
          date: entry.date,
          time: entry.time || entry.timestamp
        })
      });
    } catch (error) {
      console.error('Error saving journal entry:', error);
    }
  };

  // Delete journal entry from API
  const deleteJournalFromAPI = async (entryId) => {
    try {
      await fetch(`${API_URL}/api/aether/journal/${String(entryId)}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Error deleting journal entry:', error);
    }
  };

  // ===== TASKS API FUNCTIONS =====
  const fetchTasks = async () => {
    try {
      setTasksLoading(true);
      const res = await fetch(`${API_URL}/api/aether/tasks`);
      if (res.ok) {
        const data = await res.json();
        // Map task_id to id for frontend consistency
        const mappedData = data.map(task => ({
          ...task,
          id: task.task_id || task.id
        }));
        setTasks(mappedData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setTasksLoading(false);
    }
  };

  const saveTaskToAPI = async (task) => {
    try {
      await fetch(`${API_URL}/api/aether/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task_id: String(task.id),
          text: task.text,
          completed: task.completed || false,
          created_at: task.created_at
        })
      });
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  const updateTaskInAPI = async (taskId, completed) => {
    try {
      await fetch(`${API_URL}/api/aether/tasks/${String(taskId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed })
      });
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const deleteTaskFromAPI = async (taskId) => {
    try {
      await fetch(`${API_URL}/api/aether/tasks/${String(taskId)}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const addTask = (text) => {
    const newTask = {
      id: String(Date.now()),
      text: text,
      completed: false,
      created_at: new Date().toISOString()
    };
    setTasks(prev => [newTask, ...prev]);
    saveTaskToAPI(newTask);
  };

  const toggleTask = (taskId) => {
    setTasks(prev => prev.map(task => {
      if (task.id === taskId) {
        const newCompleted = !task.completed;
        updateTaskInAPI(taskId, newCompleted);
        return { ...task, completed: newCompleted };
      }
      return task;
    }));
  };

  const deleteTask = (taskId) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
    deleteTaskFromAPI(taskId);
  };

  // Load tasks on mount
  useEffect(() => {
    fetchTasks();
  }, []);

  // Admin-lock persistence:
  // We used to wipe `aether_admin_authenticated` on every page-unload, which
  // forced the admin-password prompt after every refresh — annoying given
  // we already have the Aether JWT + OTP gate in front. The lock now stays
  // until you explicitly hit 'Abmelden' (which clears both the Aether token
  // and the admin flag).

  // Fetch Admin Data when authenticated
  useEffect(() => {
    if (isAdminAuthenticated) {
      fetchAdminData();
    }
  }, [isAdminAuthenticated]);

  const fetchAdminData = async () => {
    try {
      // All admin endpoints are JWT-gated — must go through authFetch.
      const [statsRes, affiliatesRes, codesRes, ordersRes, emailsRes, activityRes, archiveRes] = await Promise.all([
        authFetch('/api/admin/stats'),
        authFetch('/api/affiliates'),               // admin-only now (JWT-gated)
        authFetch('/api/discount-codes'),           // admin-only now (JWT-gated)
        authFetch('/api/admin/orders'),
        authFetch('/api/admin/emails?limit=200'),
        authFetch('/api/admin/activity?limit=300'),
        authFetch('/api/admin/archive?limit=200'),
      ]);

      setAdminStats(await statsRes.json());
      setAffiliates(await affiliatesRes.json());
      setDiscountCodes(await codesRes.json());
      setOrders(await ordersRes.json());
      const emails = await emailsRes.json();
      setEmailLog(Array.isArray(emails) ? emails : []);
      const activity = await activityRes.json().catch(() => []);
      setActivityLog(Array.isArray(activity) ? activity : []);
      const archive = await archiveRes.json().catch(() => []);
      setArchiveItems(Array.isArray(archive) ? archive : []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    }
  };

  // ========== LICENSE SYSTEM FUNCTIONS ==========
  const loginToLicenseSystem = async () => {
    try {
      const res = await fetch(`${API_URL}/api/licenses/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          username: 'david',  // Uses same password as admin
          password: adminPassword 
        })
      });
      if (res.ok) {
        const data = await res.json();
        setLicenseToken(data.access_token);
        try { localStorage.setItem('aether_license_token', data.access_token); } catch {}
        return data.access_token;
      }
      return null;
    } catch (error) {
      console.error('License login error:', error);
      return null;
    }
  };

  const fetchLicenseData = async (token) => {
    if (!token) return;
    setLicenseLoading(true);
    try {
      const params = new URLSearchParams();
      if (licenseSearch) params.append('search', licenseSearch);
      if (licenseProductFilter !== 'all') params.append('product', licenseProductFilter);
      if (licenseStatusFilter !== 'all') params.append('status', licenseStatusFilter);
      if (licenseTypeFilter !== 'all') params.append('key_type', licenseTypeFilter);
      
      const [keysRes, statsRes, productsRes] = await Promise.all([
        fetch(`${API_URL}/api/licenses/admin/keys?${params.toString()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/licenses/admin/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/licenses/admin/products`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      // If any call comes back 401 the cached license-token is stale —
      // drop it so the next render-cycle reprompts for the admin password.
      if (keysRes.status === 401 || statsRes.status === 401 || productsRes.status === 401) {
        try { localStorage.removeItem('aether_license_token'); } catch {}
        setLicenseToken(null);
        setLicenses([]);
        setLicenseStats(null);
        return;
      }
      if (keysRes.ok) {
        const keysData = await keysRes.json();
        setLicenses(keysData.items || []);
      }
      if (statsRes.ok) {
        setLicenseStats(await statsRes.json());
      }
      if (productsRes.ok) {
        setLicenseProducts(await productsRes.json());
      }
    } catch (error) {
      console.error('Error fetching license data:', error);
    } finally {
      setLicenseLoading(false);
    }
  };

  const createLicense = async () => {
    if (!licenseToken) return;
    try {
      const res = await fetch(`${API_URL}/api/licenses/admin/keys`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${licenseToken}`
        },
        body: JSON.stringify(newLicense)
      });

      if (res.ok) {
        const newKey = await res.json();
        setLicenses(prev => [newKey, ...prev]);
        // Reset to fresh perpetual single — explicit null for expires_in_days
        // so a previous test-licence value doesn't bleed into the next form.
        setNewLicense({ key_type: 'single', products: 'mediaflow', note: '', expires_in_days: null });
        fetchLicenseData(licenseToken); // Refresh stats
      }
    } catch (error) {
      console.error('Error creating license:', error);
      setErrorMessage('Fehler beim Erstellen der Lizenz');
    }
  };

  // Admin grant: gives a product to an arbitrary email (free of charge).
  // Backend creates a real license_key + transaction so the customer
  // sees the product in their /konto/lizenzen library at next login.
  const handleAdminGrant = async () => {
    if (!grantForm.email.trim() || !grantForm.product_id) return;
    setGranting(true);
    setGrantResult(null);
    try {
      const res = await authFetch('/api/admin/grant-product', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: grantForm.email.trim().toLowerCase(),
          product_id: grantForm.product_id,
          license_type: grantForm.license_type,
          note: grantForm.note.trim() || null,
          gueltig_tage: Number(grantForm.gueltig_tage),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setGrantResult({ ok: false, error: data.detail || 'Freischalten fehlgeschlagen' });
      } else {
        setGrantResult({ ok: true, ...data });
        setGrantForm((f) => ({ ...f, email: '', note: '' }));
        // Refresh license list so the new key shows up.
        if (licenseToken) fetchLicenseData(licenseToken);
      }
    } catch (e) {
      setGrantResult({ ok: false, error: String(e?.message || e) });
    } finally {
      setGranting(false);
    }
  };

  const toggleLicense = async (keyId) => {
    if (!licenseToken) return;
    try {
      const res = await fetch(`${API_URL}/api/licenses/admin/keys/${keyId}/toggle`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${licenseToken}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setLicenses(prev => prev.map(l => 
          l.id === keyId ? { ...l, is_active: data.is_active } : l
        ));
      }
    } catch (error) {
      console.error('Error toggling license:', error);
    }
  };

  const deleteLicense = async (keyId) => {
    // Grund ist Pflicht. Ohne ihn laesst sich Monate spaeter nicht mehr klaeren,
    // warum ein Kunde seine Lizenz verloren hat — und der Server nimmt es auch
    // gar nicht mehr an.
    const grund = window.prompt(
      'Lizenz löschen — warum?\n\nDer Grund landet mit deinem Namen im Archiv und im Protokoll.\n' +
      'Die Lizenz wird aus dem Lizenzsystem entfernt und funktioniert sofort nicht mehr.'
    );
    if (grund === null) return;
    if (grund.trim().length < 3) { window.alert('Bitte einen Grund angeben (mindestens 3 Zeichen).'); return; }
    try {
      // New soft-delete endpoint: archives the license in MongoDB before
      // removing it from PostgreSQL. Restorable from the Archiv tab.
      const res = await authFetch(`/api/admin/licenses/${keyId}?grund=${encodeURIComponent(grund.trim())}`, { method: 'DELETE' });
      if (res.ok) {
        setLicenses(prev => prev.filter(l => l.id !== keyId));
        fetchLicenseData(licenseToken); // Refresh stats
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || 'Löschen fehlgeschlagen');
      }
    } catch (error) {
      console.error('Error deleting license:', error);
    }
  };

  const viewLicenseDetail = async (keyId) => {
    if (!licenseToken) return;
    try {
      const res = await fetch(`${API_URL}/api/licenses/admin/keys/${keyId}`, {
        headers: { 'Authorization': `Bearer ${licenseToken}` }
      });
      
      if (res.ok) {
        setSelectedLicense(await res.json());
        setShowLicenseDetail(true);
      }
    } catch (error) {
      console.error('Error fetching license detail:', error);
    }
  };

  const removeActivation = async (keyId, machineId) => {
    if (!licenseToken) return;
    try {
      const res = await fetch(`${API_URL}/api/licenses/admin/keys/${keyId}/activations/${encodeURIComponent(machineId)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${licenseToken}` }
      });
      
      if (res.ok) {
        // Refresh the detail view
        viewLicenseDetail(keyId);
        fetchLicenseData(licenseToken);
      }
    } catch (error) {
      console.error('Error removing activation:', error);
    }
  };

  // Auto-login to license system when admin is authenticated
  useEffect(() => {
    if (isAdminAuthenticated && adminActiveTab === 'licenses' && !licenseToken) {
      loginToLicenseSystem().then(token => {
        if (token) fetchLicenseData(token);
      });
    }
  }, [isAdminAuthenticated, adminActiveTab]);

  // Load the Aether user list for the grant email picker when the
  // Lizenzen tab opens. Cheap, runs once per tab visit.
  useEffect(() => {
    if (!isAdminAuthenticated || (adminActiveTab !== 'licenses' && adminActiveTab !== 'gifts')) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch('/api/aether/auth/users');
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setGrantUsers(Array.isArray(data.items) ? data.items : []);
      } catch { /* ignore — free-text email entry still works */ }
    })();
    return () => { cancelled = true; };
  }, [isAdminAuthenticated, adminActiveTab, authFetch]);

  // Refetch licenses when filters change
  useEffect(() => {
    if (licenseToken) {
      fetchLicenseData(licenseToken);
    }
  }, [licenseSearch, licenseProductFilter, licenseStatusFilter, licenseTypeFilter]);

  const handleNavClick = (id) => {
    // Check if trying to access admin
    if (id === 'admin') {
      if (!isAdmin) {
        // Hard block: partners cannot access admin even by URL fiddling
        return;
      }
      if (!isAdminAuthenticated) {
        setShowAdminAuth(true);
        return;
      }
    }
    setActiveNav(id);
    setShowMobileSidebar(false);
    setShowMobileVault(false);
  };
  
  // Step 1: persist the version + download URL. NO emails are sent.
  // Customers see the new version in their library right away; the
  // announcement happens separately when admin clicks Notify.
  const handleSaveVersion = async () => {
    setUpdateSaving(true);
    setUpdateResult(null);
    try {
      const res = await authFetch(
        `/api/admin/products/${updateForm.product_id}/save-version`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            version: updateForm.version.trim(),
            download_url: updateForm.download_url.trim(),
            changelog: updateForm.changelog.trim(),
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setUpdateResult({ kind: 'saved', ok: false, error: data.detail || 'Speichern fehlgeschlagen' });
      } else {
        setUpdateResult({ kind: 'saved', ok: true, ...data });
      }
    } catch (e) {
      setUpdateResult({ kind: 'saved', ok: false, error: String(e?.message || e) });
    } finally {
      setUpdateSaving(false);
    }
  };

  // Step 2 (separate): blast an email to every paying customer of the
  // chosen product using whatever version is currently saved on the
  // server. Safe to retry — re-sends to everyone.
  const handleNotifyCustomers = async () => {
    if (!window.confirm(
      `Wirklich allen Käufern von "${updateForm.product_id}" eine Update-Mail mit dem gespeicherten Download-Link verschicken?`
    )) return;
    setUpdateNotifying(true);
    setUpdateResult(null);
    try {
      const res = await authFetch(
        `/api/admin/products/${updateForm.product_id}/notify-customers`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setUpdateResult({ kind: 'notified', ok: false, error: data.detail || 'Versand fehlgeschlagen' });
      } else {
        setUpdateResult({ kind: 'notified', ok: true, ...data });
      }
    } catch (e) {
      setUpdateResult({ kind: 'notified', ok: false, error: String(e?.message || e) });
    } finally {
      setUpdateNotifying(false);
    }
  };

  const handleAdminLogin = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPassword })
      });
      
      if (res.ok) {
        setIsAdminAuthenticated(true);
        localStorage.setItem('aether_admin_authenticated', 'true'); // Persist session
        setShowAdminAuth(false);
        setAdminError('');
        setActiveNav('admin');
      } else {
        setAdminError('Falsches Passwort');
      }
    } catch (error) {
      setAdminError('Verbindungsfehler');
    }
  };

  // Admin API Functions
  const resendEmail = async (sessionId) => {
    setResendingEmail(sessionId);
    setResendSuccess(null);
    try {
      const res = await authFetch(`/api/admin/resend-email/${sessionId}`, { method: 'POST' });
      if (res.ok) {
        setResendSuccess(sessionId);
        setTimeout(() => setResendSuccess(null), 3000);
      }
    } catch (error) {
      console.error('Error resending email:', error);
    } finally {
      setResendingEmail(null);
    }
  };

  const createAffiliate = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch('/api/affiliates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAffiliate)
      });
      if (res.ok) {
        setNewAffiliate({ name: '', email: '' });
        fetchAdminData();
      }
    } catch (error) {
      console.error('Error creating affiliate:', error);
    }
  };

  const deleteAffiliate = async (id) => {
    if (!id) { console.warn('deleteAffiliate called without id'); return; }
    if (!window.confirm('Affiliate löschen? (Landet im Archiv und kann wiederhergestellt werden.)')) return;
    try {
      const res = await authFetch(`/api/affiliates/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      fetchAdminData();
    } catch (error) {
      console.error('Error deleting affiliate:', error);
      window.alert('Konnte Affiliate nicht löschen: ' + error.message);
    }
  };

  const createDiscountCode = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newDiscount,
        product_id: newDiscount.scope === 'specific_product' ? newDiscount.product_id : null,
        max_uses: newDiscount.max_uses ? parseInt(newDiscount.max_uses) : null,
        expires_at: newDiscount.expires_at || null,
        scope: newDiscount.scope
      };
      const res = await authFetch('/api/discount-codes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setNewDiscount({ code: '', discount_percent: 10, product_id: '', max_uses: '', expires_at: '', scope: 'all_products' });
        fetchAdminData();
      }
    } catch (error) {
      console.error('Error creating discount code:', error);
    }
  };

  const deleteDiscountCode = async (id) => {
    if (!id) return;
    if (!window.confirm('Rabattcode löschen? (Landet im Archiv und kann wiederhergestellt werden.)')) return;
    try {
      const res = await authFetch(`/api/discount-codes/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      fetchAdminData();
    } catch (error) {
      console.error('Error deleting discount code:', error);
      window.alert('Konnte Code nicht löschen: ' + error.message);
    }
  };

  const toggleDiscountCode = async (id) => {
    try {
      await authFetch(`/api/discount-codes/${id}/toggle`, { method: 'PUT' });
      fetchAdminData();
    } catch (error) {
      console.error('Error toggling discount code:', error);
    }
  };

  // ===== NEW: order/email delete + archive restore + broadcast =====
  const deleteOrder = async (sessionId) => {
    // Bei Bestellungen umso wichtiger: dahinter steht Geld.
    const grund = window.prompt(
      'Bestellung archivieren — warum?\n\nDer Grund landet mit deinem Namen im Archiv und im Protokoll.'
    );
    if (grund === null) return;
    if (grund.trim().length < 3) { window.alert('Bitte einen Grund angeben (mindestens 3 Zeichen).'); return; }
    try {
      const res = await authFetch(`/api/admin/orders/${sessionId}?grund=${encodeURIComponent(grund.trim())}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      fetchAdminData();
    } catch (e) { console.error(e); window.alert('Konnte Bestellung nicht archivieren: ' + e.message); }
  };

  const deleteEmailLog = async (logId) => {
    if (!window.confirm('E-Mail-Eintrag archivieren?')) return;
    try {
      const res = await authFetch(`/api/admin/emails/${logId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      fetchAdminData();
    } catch (e) { console.error(e); window.alert('Konnte Eintrag nicht archivieren: ' + e.message); }
  };

  const restoreArchived = async (archiveId, label) => {
    if (!window.confirm(`„${label || 'Eintrag'}" aus dem Archiv wiederherstellen?`)) return;
    try {
      const res = await authFetch(`/api/admin/restore/${archiveId}`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      fetchAdminData();
    } catch (e) { console.error(e); window.alert('Wiederherstellung fehlgeschlagen: ' + e.message); }
  };

  const sendBroadcastEmail = async ({ dryRun }) => {
    if (!broadcastForm.subject.trim() || !broadcastForm.body_html.trim()) {
      window.alert('Bitte Betreff und Inhalt ausfüllen.');
      return;
    }
    setBroadcastBusy(true);
    setBroadcastResult(null);
    try {
      const res = await authFetch('/api/admin/broadcast-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...broadcastForm, dry_run: !!dryRun }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setBroadcastResult(data);
      if (!dryRun) fetchAdminData();   // refresh email log
    } catch (e) {
      console.error(e);
      setBroadcastResult({ error: e.message });
    } finally {
      setBroadcastBusy(false);
    }
  };

  const copyAdminToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setAdminCopiedCode(id);
    setTimeout(() => setAdminCopiedCode(null), 2000);
  };

  const adminLogout = () => {
    setIsAdminAuthenticated(false);
    setActiveNav('01');
  };

  const handleStyleSelect = (preset) => {
    setSelectedStyle(preset);
    setActiveNav('01'); 
    setShowPresetModal(false); 
    setShowMobileSidebar(false);
  };

  // Scriptum: Show confirmation before posting
  const handleScriptumPost = () => {
    if (!notes.trim()) return;
    setPendingNote(notes);
    setShowScriptumConfirm(true);
  };

  // Scriptum: Confirm and post to selected target(s)
  const confirmScriptumPost = () => {
    if (!pendingNote.trim()) return;
    
    const timestamp = new Date();
    
    if (scriptumTarget === 'journal' || scriptumTarget === 'both') {
      const newEntry = {
        id: String(Date.now()), 
        text: pendingNote,
        time: timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        date: timestamp.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })
      };
      setJournal(prev => [newEntry, ...prev]);
      saveJournalToAPI(newEntry);
    }
    
    if (scriptumTarget === 'tasks' || scriptumTarget === 'both') {
      addTask(pendingNote);
    }
    
    setNotes('');
    setPendingNote('');
    setShowScriptumConfirm(false);
  };

  // Legacy function kept for compatibility
  const addNoteToJournal = () => {
    handleScriptumPost();
  };

  const exportConcept = () => {
    const journalText = journal.map(e => `[${e.date} ${e.timestamp}] ${e.text}`).join('\n');
    const report = `AETHER LAB - REPORT\n\nMANIFEST:\n${safeRender(labState.manifesto)}\n\nCHRONOS JOURNAL:\n${journalText}\n\nNOTIZEN:\n${notes}`;
    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `AETHER_EXPORT_${labState.id}.txt`; link.click();
    URL.revokeObjectURL(url);
  };

  const generateInspiration = async () => {
    setExtLoading(prev => ({ ...prev, inspiration: true }));
    try {
      // Use backend proxy for all AI calls
      const res = await generateTextViaBackend(
        "Generiere einen einzigen, extrem minimalistischen und poetischen Design-Prompt. Nur den Prompt ausgeben. Sprache: Deutsch.",
        null,
        selectedModel,
        150
      );
      setPrompt(res.content?.trim().replace(/^"(.*)"$/, '$1') || "");
    } catch (e) { 
      console.error(e); 
      setErrorMessage(e.message || 'Inspiration konnte nicht generiert werden.');
    } finally { setExtLoading(prev => ({ ...prev, inspiration: false })); }
  };

  // Chat function for conversational AI
  const sendChatMessage = async () => {
    if (!prompt.trim() || chatLoading) return;
    
    const userMessage = { role: 'user', content: prompt, timestamp: new Date().toLocaleTimeString() };
    setChatMessages(prev => [...prev, userMessage]);
    setPrompt('');
    setChatLoading(true);
    
    try {
      // Build conversation history for context
      const conversationHistory = chatMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      conversationHistory.push({ role: 'user', content: userMessage.content });
      
      // Use backend proxy to avoid CORS
      const res = await fetch(`${API_URL}/api/aether/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: conversationHistory,
          model: selectedModel
        })
      });
      
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'API Fehler');
      }
      
      const data = await res.json();
      const assistantResponse = data.content || 'Keine Antwort erhalten.';
      
      const botMessage = { role: 'assistant', content: assistantResponse, timestamp: new Date().toLocaleTimeString() };
      setChatMessages(prev => [...prev, botMessage]);
      
      // Save chat to records
      const chatRecord = {
        id: String(Date.now()),
        manifesto: `Chat: ${userMessage.content.substring(0, 50)}...`,
        type: 'chat',
        chatMessages: [...chatMessages, userMessage, botMessage],
        timestamp: new Date().toLocaleTimeString(),
        version: '1.0'
      };
      syncHistory(chatRecord);
      
    } catch (e) {
      console.error('Chat error:', e);
      setErrorMessage(e.message || 'Chat-Nachricht konnte nicht gesendet werden.');
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Fehler: ' + (e.message || 'Unbekannter Fehler'), timestamp: new Date().toLocaleTimeString(), isError: true }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Clear chat history
  const clearChat = () => {
    setChatMessages([]);
  };

  const performFullSynthesis = async () => {
    if ((!prompt && !selectedStyle) || loading || imageLoading) return;
    
    const isTextMode = generationMode === 'text' || generationMode === 'both';
    const isImageMode = generationMode === 'image' || generationMode === 'both';
    
    setLoading(true); 
    if (isTextMode) setLoadingPhase('Denke...');
    if (isImageMode && !isTextMode) setLoadingPhase('Visualisierung...');
    if (isImageMode) setImageLoading(true);
    
    const newId = Date.now();
    let conceptPrompt = prompt ? `${prompt} ${selectedStyle ? `(Stil: ${selectedStyle.seed})` : ''}` : (selectedStyle?.seed || prompt);
    
    try {
      // Only generate text if in text or both mode
      let parsed = isTextMode ? { manifesto: conceptPrompt, analysis: '', tags: [] } : { manifesto: '', analysis: '', tags: [] };
      
      // Text Generation (if text or both mode) - using backend proxy
      if (isTextMode) {
        const textRes = await generateTextViaBackend(
          `Konzept: ${conceptPrompt}`,
          "Erstelle ein minimalistisches Konzept. Antworte NUR mit JSON: { manifesto, analysis, tags[3] }. Deutsch.",
          selectedModel,
          500,
          selectedModel === 'gemini'
        );
        parsed = extractJSON(textRes.content || '{}');
      }
      
      const step1 = { 
        ...parsed, 
        id: newId, 
        image: null, 
        emotionMap: null, 
        vorhersage: "", 
        distillation: "", 
        visualCritique: "", 
        expansion: "", 
        soul: "", 
        blueprint: "", 
        model: selectedModel,
        type: generationMode
      };
      
      // If text-only mode, save and return
      if (!isImageMode) {
        setLabState(step1); 
        syncHistory(step1);
        setPrompt(''); 
        setSelectedStyle(null);
        setLoading(false);
        return;
      }
      
      // For image-only mode, don't save intermediate state with text
      if (isTextMode) {
        setLabState(step1); 
        syncHistory(step1);
      }
      setLoadingPhase('Visualisierung...');
      
      // Image Generation (if image or both mode) - using backend proxy
      // For image-only mode, use the original prompt directly
      // Respect each style's palette: 'color' for Pixar-style presets, otherwise default to AETHER monochrome
      const conceptText = isTextMode ? (parsed.manifesto?.substring(0,100) || conceptPrompt.substring(0,100)) : conceptPrompt.substring(0,100);
      const paletteSuffix = selectedStyle?.palette === 'color' ? 'vibrant color, highly detailed' : 'monochrome';
      // Default negative prompt prevents common DALL-E artifacts (split frames, watermarks)
      const defaultNegatives = 'no text, no watermark, no logo, no split frame, no collage, no border, single image';
      const userNegatives = negativePrompt.trim();
      const negativeSuffix = userNegatives 
        ? `, avoid: ${defaultNegatives}, ${userNegatives}`
        : `, avoid: ${defaultNegatives}`;
      const imgPrompt = selectedStyle 
        ? `${selectedStyle.style}, concept: ${conceptText}, ${paletteSuffix}, 8k${negativeSuffix}`
        : `Hyper-minimalist monochrome, concept: ${conceptText}, 8k${negativeSuffix}`;
      
      const imgRes = await generateImageViaBackend(imgPrompt, selectedModel, imageFormat);
      const imageBase64 = imgRes.image;
      
      // For image-only mode, create a clean state without text content
      const finalState = isTextMode 
        ? { ...step1, image: imageBase64 }
        : { 
            id: newId, 
            manifesto: '', 
            analysis: '', 
            tags: [], 
            image: imageBase64, 
            emotionMap: null, 
            vorhersage: "", 
            distillation: "", 
            visualCritique: "", 
            expansion: "", 
            soul: "", 
            blueprint: "", 
            model: selectedModel,
            type: generationMode
          };
      setLabState(finalState); 
      syncHistory(finalState);
      setPrompt(''); 
      setSelectedStyle(null);
    } catch (e) { 
      console.error(e); 
      setErrorMessage(e.message || 'Ein unbekannter Fehler ist aufgetreten.'); 
    } finally { 
      setLoading(false); 
      setImageLoading(false); 
      setLoadingPhase(''); 
    }
  };

  const runExtension = async (type) => {
    if (!labState.manifesto || extLoading[type]) return;
    setExtLoading(prev => ({ ...prev, [type]: true }));
    
    let sys = "";
    if (type === 'emotion') sys = "Analysiere Emotion JSON { primary_emotion, empathy_score }. Deutsch.";
    else if (type === 'vorhersage') sys = "Prophezeie Einfluss in 50 Jahren. 1 Satz. Deutsch.";
    else if (type === 'distill') sys = "Reduziere auf EIN deutsches Substantiv. Nur das Wort.";
    else if (type === 'expansion') sys = "Philosophische Abhandlung (4 Sätze). Deutsch.";
    else if (type === 'soul') sys = "Wer lebt hier? Profil (Name, Wesen, Zweck). Deutsch.";
    else if (type === 'blueprint') sys = "Blueprint (Materialien, Statik). Deutsch.";
    
    try {
      // Use backend proxy for all AI calls
      const data = await generateTextViaBackend(
        safeRender(labState.manifesto),
        sys,
        selectedModel,
        300,
        type === 'emotion'
      );
      const text = data.content || '';
      
      const res = type === 'emotion' ? extractJSON(text) : text;
      const next = { ...labState, [type === 'distill' ? 'distillation' : type]: res };
      if (type === 'distill') next.distillation = String(res).replace(/[^\wäöüÄÖÜß]/g, '').toUpperCase();
      setLabState(next); syncHistory(next);
    } catch (e) { 
      console.error(e); 
      setErrorMessage(e.message || 'Erweiterung konnte nicht generiert werden.');
    } finally { setExtLoading(prev => ({ ...prev, [type]: false })); }
  };

  const regenerateVisual = async () => {
    if (!labState.manifesto || imageLoading) return;
    setImageLoading(true);
    setLoadingPhase('Neuberechnung...');
    
    try {
      const imgPrompt = `Hyper-minimalist abstract monochrome masterpiece, reflecting: ${labState.manifesto.substring(0, 150)}, 8k`;
      
      // Use backend proxy for image generation
      const imgRes = await generateImageViaBackend(imgPrompt, selectedModel, imageFormat);
      const imageBase64 = imgRes.image;
      
      setLabState(prev => {
        const next = { ...prev, image: imageBase64 };
        syncHistory(next);
        return next;
      });
    } catch (e) { 
      console.error(e); 
      setErrorMessage(e.message || 'Bild konnte nicht neu generiert werden.');
    } finally { setImageLoading(false); setLoadingPhase(''); }
  };

  const handleDownloadImage = (imgData, id) => {
    if (!imgData) return;
    const link = document.createElement('a');
    link.href = imgData;
    link.download = `AETHER_VISION_${id}.png`;
    link.click();
  };

  const restoreFromHistory = (item) => { 
    setLabState(item); 
    // If the item has chat messages, load them and switch to chat mode
    if (item.type === 'chat' && item.chatMessages) {
      setChatMessages(item.chatMessages);
      setGenerationMode('chat');
    }
    handleNavClick('01'); 
  };

  // Start new chat (clear current chat and reset)
  const startNewChat = () => {
    setChatMessages([]);
    setLabState({
      id: Date.now(), version: "1.0", manifesto: "", emotionMap: null, vorhersage: "", 
      distillation: "", visualCritique: "", expansion: "", soul: "", blueprint: "", tags: [], image: null
    });
    setPrompt('');
  };

  const resetLab = () => {
    setLabState({
      id: Date.now(), version: "1.0", manifesto: "", emotionMap: null, vorhersage: "", 
      distillation: "", visualCritique: "", expansion: "", soul: "", blueprint: "", tags: [], image: null
    });
    setPrompt('');
    setSelectedStyle(null);
  };

  const SelectedStyleIcon = selectedStyle ? selectedStyle.icon : null;

  return (
    <>
    {/* Die Kopfleiste der Website gilt auch hier: das Portal ist keine
        andere Welt, und ohne sie kam man nur ueber den Zurueck-Knopf des
        Browsers wieder in den Shop. Sie schwebt ueber dem Layout, deshalb
        ruecken die mobilen Knoepfe unten weiter nach unten. */}
    <FloatingHeader />
    <div className="aether-container relative flex h-screen w-full pt-20 text-white font-sans overflow-hidden">
      <style>{glassStyles}</style>
      <style>{`
        /* ============= Admin Dashboard hover-tooltips =============
           Any element with data-tip="..." gets a styled tooltip on hover.
           Replaces ugly native browser title-tooltips and works on icon-only buttons.
        */
        [data-tip] { position: relative; }
        [data-tip]::after {
          content: attr(data-tip);
          position: absolute;
          bottom: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%) translateY(4px);
          background: rgba(12,12,12,0.97);
          border: 1px solid rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.85);
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 10px;
          line-height: 1.4;
          font-family: 'Space Mono', monospace;
          letter-spacing: 0.03em;
          max-width: 240px;
          width: max-content;
          text-align: center;
          white-space: normal;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.18s ease 0.15s, transform 0.18s ease 0.15s;
          z-index: 1000;
          box-shadow: 0 8px 24px rgba(0,0,0,0.5);
        }
        [data-tip]:hover::after,
        [data-tip]:focus-visible::after {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }
        /* tip-pos="below" → tooltip appears under the element (use for elements near the top) */
        [data-tip][data-tip-pos="below"]::after {
          bottom: auto;
          top: calc(100% + 8px);
          transform: translateX(-50%) translateY(-4px);
        }
        [data-tip][data-tip-pos="below"]:hover::after,
        [data-tip][data-tip-pos="below"]:focus-visible::after {
          transform: translateX(-50%) translateY(0);
        }
        /* tip-pos="right" / "left" for buttons against an edge */
        [data-tip][data-tip-pos="left"]::after {
          bottom: 50%;
          top: 50%;
          left: auto;
          right: calc(100% + 8px);
          transform: translateY(-50%) translateX(4px);
        }
        [data-tip][data-tip-pos="left"]:hover::after,
        [data-tip][data-tip-pos="left"]:focus-visible::after {
          transform: translateY(-50%) translateX(0);
        }
        /* Suppress on touch — hover doesn't make sense there */
        @media (hover: none) { [data-tip]::after { display: none; } }
      `}</style>

      {/* Optional: Generated image as soft accent overlay on top of the global VortexBackground */}
      {labState.image && (
        <div className="fixed inset-0 z-0 pointer-events-none transition-opacity duration-1000">
          <img src={labState.image} className="w-full h-full object-cover opacity-20 blur-[80px] scale-125 transition-all duration-1000 ease-in-out" alt="BG" />
          <div className="absolute inset-0 bg-[#050505]/40" />
        </div>
      )}

      {/* Admin Password Modal */}
      {showAdminAuth && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="monolith-heavy p-8 w-full max-w-sm mx-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Lock size={20} className="text-white/60" />
                <span className="font-mono text-sm uppercase tracking-widest">Admin Zugang</span>
              </div>
              <button onClick={() => setShowAdminAuth(false)} className="text-white/40 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <input
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdminLogin()}
              placeholder="Passwort eingeben..."
              className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-white font-mono text-sm outline-none focus:border-white/30 mb-4"
            />
            {adminError && <p className="text-red-400 text-xs font-mono mb-4">{adminError}</p>}
            <button
              onClick={handleAdminLogin}
              className="w-full monolith-glass py-3 text-center font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-black transition-all"
            >
              Anmelden
            </button>
          </div>
        </div>
      )}

      {/* Error Popup */}
      {errorMessage && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="monolith-heavy p-6 w-full max-w-md mx-4 border border-red-500/30">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <X size={16} className="text-red-400" />
                </div>
                <span className="font-mono text-sm uppercase tracking-widest text-red-400">Fehler</span>
              </div>
              <button onClick={() => setErrorMessage(null)} className="text-white/40 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <p className="text-white/80 font-mono text-sm leading-relaxed mb-4">{errorMessage}</p>
            <button
              onClick={() => setErrorMessage(null)}
              className="w-full monolith-glass py-3 text-center font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-black transition-all"
            >
              Verstanden
            </button>
          </div>
        </div>
      )}

      {/* License Detail Modal */}
      {showLicenseDetail && selectedLicense && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="monolith-heavy p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto aether-scrollbar">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Key size={20} className="text-white/60" />
                <span className="font-mono text-sm uppercase tracking-widest">Lizenz Details</span>
              </div>
              <button onClick={() => { setShowLicenseDetail(false); setSelectedLicense(null); }} className="text-white/40 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Key Info */}
              <div className="monolith-glass p-4">
                <div className="flex items-center gap-2 mb-4">
                  <code className="font-mono text-lg text-white bg-white/5 px-3 py-1 rounded">{selectedLicense.key}</code>
                  <button
                    onClick={() => navigator.clipboard.writeText(selectedLicense.key)}
                    className="text-white/30 hover:text-white p-2"
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-mono text-[9px] uppercase text-white/40">Typ</span>
                    <p className="font-mono text-white capitalize">{selectedLicense.key_type}</p>
                  </div>
                  <div>
                    <span className="font-mono text-[9px] uppercase text-white/40">Produkte</span>
                    <p className="font-mono text-white">{selectedLicense.products}</p>
                  </div>
                  <div>
                    <span className="font-mono text-[9px] uppercase text-white/40">Aktivierungen</span>
                    <p className="font-mono text-white">{selectedLicense.current_activations} / {selectedLicense.max_activations}</p>
                  </div>
                  <div>
                    <span className="font-mono text-[9px] uppercase text-white/40">Status</span>
                    <p className={`font-mono ${selectedLicense.is_active ? 'text-green-400' : 'text-red-400'}`}>
                      {selectedLicense.is_active ? 'Aktiv' : 'Inaktiv'}
                    </p>
                  </div>
                  {selectedLicense.note && (
                    <div className="col-span-2">
                      <span className="font-mono text-[9px] uppercase text-white/40">Notiz</span>
                      <p className="font-mono text-white/70">{selectedLicense.note}</p>
                    </div>
                  )}
                  <div className="col-span-2">
                    <span className="font-mono text-[9px] uppercase text-white/40">Erstellt am</span>
                    <p className="font-mono text-white/70">{selectedLicense.created_at ? new Date(selectedLicense.created_at).toLocaleString('de-DE') : '-'}</p>
                  </div>
                </div>
              </div>

              {/* Activations */}
              <div>
                <h4 className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-3">
                  Aktivierte Geräte ({selectedLicense.activations?.length || 0})
                </h4>
                {selectedLicense.activations && selectedLicense.activations.length > 0 ? (
                  <div className="space-y-2">
                    {selectedLicense.activations.map((act, idx) => (
                      <div key={idx} className="monolith-glass p-3 flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-xs text-white truncate">{act.machine_name || 'Unbekanntes Gerät'}</p>
                          <p className="font-mono text-[9px] text-white/40 truncate">{act.machine_id}</p>
                          <p className="font-mono text-[9px] text-white/30">
                            Zuletzt: {act.last_seen ? new Date(act.last_seen).toLocaleString('de-DE') : '-'}
                          </p>
                        </div>
                        <button
                          onClick={() => removeActivation(selectedLicense.id, act.machine_id)}
                          className="p-2 text-white/20 hover:text-red-400 transition-colors shrink-0"
                          title="Aktivierung entfernen"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="font-mono text-[10px] text-white/30 text-center py-4">Keine aktiven Geräte</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preset/Style Selection Modal */}
      {showPresetModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="monolith-heavy w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <Palette size={20} className="text-white/60" />
                <span className="font-mono text-sm uppercase tracking-widest">Stil wählen</span>
              </div>
              <button onClick={() => setShowPresetModal(false)} className="text-white/40 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 border-b border-white/10">
              <div className="monolith-glass flex items-center px-4 py-2">
                <Search size={14} className="text-white/30 mr-3" />
                <input 
                  value={styleSearchQuery} 
                  onChange={e => setStyleSearchQuery(e.target.value)} 
                  placeholder="Stil suchen..." 
                  className="bg-transparent border-none outline-none font-mono text-[10px] uppercase w-full text-white placeholder:text-white/20" 
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto aether-scrollbar p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredPresets.map(p => {
                  const PresetIcon = p.icon;
                  return (
                    <button 
                      key={p.id} 
                      onClick={() => {
                        setSelectedStyle(p);
                        setShowPresetModal(false);
                      }}
                      className="monolith-glass group flex flex-col border-white/5 hover:border-white/30 transition-all overflow-hidden text-left h-32 relative"
                    >
                      <img src={p.img} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover grayscale opacity-30 group-hover:grayscale-0 group-hover:opacity-60 transition-all duration-500" alt={p.name} />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
                      <div className="relative z-10 p-4 flex flex-col h-full justify-end">
                        <div className="flex items-center gap-2 mb-1">
                          <PresetIcon size={12} className="text-white/60" />
                          <h3 className="font-mono text-[10px] uppercase tracking-widest text-white">{p.name}</h3>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- MOBILE DRAWERS CONTROLS --- */}
      <div className="lg:hidden fixed top-24 left-6 right-6 flex justify-between z-[120] pointer-events-none">
        <button onClick={() => setShowMobileSidebar(true)} className="p-3 monolith-glass pointer-events-auto text-white/60 hover:text-white">
          <Menu size={20} />
        </button>
        {isAdmin && (
          <button onClick={() => setShowMobileVault(true)} className="p-3 monolith-glass pointer-events-auto text-white/60 hover:text-white">
            <Palette size={20} />
          </button>
        )}
      </div>

      {/* --- LEFT SIDEBAR ---
          Width driven by --aether-left-width CSS variable on desktop only. */}
      <aside
        data-tour="sidebar"
        style={{ '--aether-left-width': `${leftSidebarWidth}px` }}
        className={`
          ${showMobileSidebar ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          mobile-drawer flex flex-col border-r border-white/10 bg-transparent p-6 transition-transform
          relative overflow-y-auto aether-scrollbar
        `}
      >
        <div className="flex justify-between items-center mb-12">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-4 group cursor-pointer"
            title="Zurück zur Hauptseite"
            data-testid="aether-logo-home"
          >
            {/* "Aether" war ein Arbeitsname. Nach außen heißt der Bereich "David Media Portal" —
                zweizeilig wie das Logo der Website, damit nichts abgeschnitten wird. */}
            <img src="/logo-badge-192.webp" alt="" className="w-8 h-8 rounded-full shrink-0 ring-2 ring-white/10" />
            <span className="flex flex-col items-start leading-none whitespace-nowrap">
              <span className="font-syne font-bold text-[15px] text-white group-hover:text-white/80 transition-colors">DAVIDMEDIA</span>
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F97316] mt-1.5">Portal</span>
            </span>
          </button>
          <button onClick={() => setShowMobileSidebar(false)} className="lg:hidden p-2 monolith-glass">
            <X size={16}/>
          </button>
        </div>

        <nav className="flex flex-col gap-1.5">
          {/* Dashboard — CUSTOMERS and PARTNERS get their dashboard tabs
              directly in this outer sidebar (no second inner sidebar). A
              partner is also a customer, so they get the same product /
              licence / order tabs plus their two affiliate tabs. Admin keeps
              a single 'Dashboard' landing entry (its tabs are inline). */}
          {(isCustomer || isPartner) ? (
            (isPartner
              ? [
                  { tab: 'overview',    label: 'Übersicht',    Icon: TrendingUp },
                  { tab: 'performance', label: 'Performance',  Icon: Activity },
                  { tab: 'products',    label: 'Produkte',     Icon: Package },
                  { tab: 'orders',      label: 'Bestellungen', Icon: ShoppingBag },
                  { tab: 'assets',      label: 'Assets',       Icon: Boxes },
                  { tab: 'community',   label: 'Community',    Icon: MessagesSquare },
                  { tab: 'profile',     label: 'Profil',       Icon: UserCog },
                ]
              : [
                  { tab: 'overview',  label: 'Übersicht',    Icon: TrendingUp },
                  { tab: 'products',  label: 'Produkte',     Icon: Package },
                  { tab: 'orders',    label: 'Bestellungen', Icon: ShoppingBag },
                  { tab: 'assets',    label: 'Assets',       Icon: Boxes },
                  { tab: 'community', label: 'Community',    Icon: MessagesSquare },
                  { tab: 'profile',   label: 'Profil',       Icon: UserCog },
                ]
            ).map(({ tab, label, Icon }) => {
              const isActive = activeNav === 'dashboard' && customerTab === tab;
              return (
                <button
                  key={tab}
                  onClick={() => { setActiveNav('dashboard'); setCustomerTab(tab); setShowMobileSidebar(false); }}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg nav-btn ${isActive ? 'active' : ''}`}
                  data-testid={`aether-nav-customer-${tab}`}
                >
                  <Icon size={16} className="shrink-0" /><span className="font-mono text-[10px] tracking-widest uppercase">{label}</span>
                </button>
              );
            })
          ) : (
            <button
              onClick={() => handleNavClick('dashboard')}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg nav-btn ${activeNav === 'dashboard' ? 'active' : ''}`}
              data-testid="aether-nav-dashboard"
            >
              <TrendingUp size={16} /><span className="font-mono text-[10px] tracking-widest uppercase">Dashboard</span>
            </button>
          )}

          {/* Assets, Community und Profil gehoeren zur Person, nicht zur Rolle —
              ein Admin hatte bisher kein eigenes Profil. */}
          {isAdmin && [
            { tab: 'produkte',  label: 'Produkte',  Icon: Package },
            { tab: 'assets',    label: 'Assets',    Icon: Boxes },
            { tab: 'community', label: 'Community', Icon: MessagesSquare },
            { tab: 'profile',   label: 'Profil',    Icon: UserCog },
          ].map(({ tab, label, Icon }) => {
            const isActive = activeNav === 'dashboard' && customerTab === tab;
            return (
              <button
                key={tab}
                onClick={() => { setActiveNav('dashboard'); setCustomerTab(tab); setShowMobileSidebar(false); }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg nav-btn ${isActive ? 'active' : ''}`}
                data-testid={`aether-nav-customer-${tab}`}
              >
                <Icon size={16} className="shrink-0" /><span className="font-mono text-[10px] tracking-widest uppercase">{label}</span>
              </button>
            );
          })}

          {/* Labor, Stile, Records und Journal standen hier mal: Werkzeuge zum
              Bildergenerieren und Notizenschreiben, die mit einem Kundenportal
              nichts zu tun haben. Wer sie sucht, findet sie in der
              Versionsgeschichte. */}

          {/* Admin section — only for admin role. Until the user authenticates
              we render the single 'Admin' lock toggle (mirrors the old UX); once
              authenticated, the sub-items expand inline with a category label. */}
          {isAdmin && (
            <>
              <div className="h-px bg-white/10 my-3" />
              {!isAdminAuthenticated ? (
                <button
                  onClick={() => handleNavClick('admin')}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg nav-btn text-white/30 hover:text-white"
                  data-testid="aether-nav-admin"
                >
                  <Settings size={16} />
                  <span className="font-mono text-[10px] tracking-widest uppercase">Admin</span>
                  <Lock size={11} className="ml-auto" />
                </button>
              ) : (
                <>
                  <p className="font-mono text-[9px] tracking-widest uppercase text-white/25 px-3 mb-1 mt-1">Admin</p>
                  {[
                    { tab: 'overview',   label: 'Übersicht',    Icon: TrendingUp },
                    { tab: 'requests',   label: 'Anfragen',     Icon: Inbox },
                    { tab: 'orders',     label: 'Bestellungen', Icon: Package },
                    { tab: 'invoices',   label: 'Rechnungen',   Icon: FileDown },
                    { tab: 'discounts',  label: 'Rabatte',      Icon: Percent },
                    { tab: 'licenses',   label: 'Lizenzen',     Icon: Key },
                    { tab: 'gifts',      label: 'Verschenken',  Icon: Gift },
                    { tab: 'emails',     label: 'E-Mails',      Icon: Mail },
                    { tab: 'affiliates', label: 'Affiliates',   Icon: Users },
                    { tab: 'users',      label: 'Nutzer',       Icon: User },
                    { tab: 'logs',       label: 'Logs',         Icon: Activity },
                    { tab: 'archive',    label: 'Archiv',       Icon: Archive },
                    { tab: 'gdpr',       label: 'DSGVO',        Icon: Shield },
                  ].map(({ tab, label, Icon }) => {
                    const isActive = activeNav === 'admin' && adminActiveTab === tab;
                    return (
                      <button
                        key={tab}
                        onClick={() => {
                          setActiveNav('admin');
                          setAdminActiveTab(tab);
                          setShowMobileSidebar(false);
                        }}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg nav-btn ${isActive ? 'active' : ''}`}
                        data-testid={`aether-nav-admin-${tab}`}
                      >
                        <Icon size={14} className="shrink-0" />
                        <span className="font-mono text-[10px] tracking-widest uppercase">{label}</span>
                      </button>
                    );
                  })}
                </>
              )}
            </>
          )}

          <div className="h-px bg-white/10 my-3" />

          {/* Aether Logout */}
          <button
            onClick={() => { aetherLogout(); localStorage.removeItem('aether_admin_authenticated'); localStorage.removeItem('aether_license_token'); }}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg nav-btn text-white/30 hover:text-white"
            data-testid="aether-nav-logout"
          >
            <LogOut size={16} />
            <span className="font-mono text-[10px] tracking-widest uppercase">Abmelden</span>
          </button>

          {/* Back to Main Site */}
          <button onClick={() => navigate('/')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg nav-btn text-white/30 hover:text-white">
            <ArrowLeft size={16} /><span className="font-mono text-[10px] tracking-widest uppercase">Zurück</span>
          </button>
        </nav>

        {/* Hier stand ein Notizblock (Scriptum) mit Journal, Tasks und einem
            Umschalter dazwischen. In einem Kundenportal hat ein persoenliches
            Notizheft nichts verloren — es war ohnehin nur fuer den Admin
            sichtbar und stand jeden Tag ungenutzt da. */}

      </aside>

      {/* Left sidebar resize handle — a 6px-wide flex item between the
          sidebar and main. Sits OUTSIDE the aside so the aside's vertical
          scrollbar doesn't swallow the click. Hidden on mobile (drawer mode). */}
      <div
        onMouseDown={startSidebarResize('left')}
        className="hidden lg:block w-1.5 cursor-col-resize group relative shrink-0 z-30"
        style={{ height: '100%' }}
        title="Ziehen zum Vergrößern"
      >
        <div className="absolute inset-0 group-hover:bg-orange-500/40 group-active:bg-orange-500/60 transition-colors" />
      </div>

      {/* --- MAIN CENTER --- */}
      <main className="flex-1 min-w-0 flex flex-col relative h-full overflow-y-auto overflow-x-hidden aether-scrollbar pb-24 lg:pb-0 z-20">
        <div className={`w-full mx-auto flex flex-col items-center justify-start p-4 sm:p-6 lg:p-12 min-h-full gap-6 sm:gap-8 mt-16 lg:mt-0 ${
          activeNav === 'admin' ? 'max-w-7xl' : 'max-w-4xl'
        }`}>

          {activeNav === 'dashboard' && (
            <div className="w-full pt-4">
              <DashboardHome
                customerTab={customerTab}
                onCustomerTab={setCustomerTab}
                onJumpToTab={(tab) => {
                  // Admin deep-links into the admin tabs (orders, users, ...).
                  // Partners now have real sidebar tabs, so a jump just selects
                  // one. Customer CTAs are self-contained and don't call this.
                  if (isAdmin) {
                    setActiveNav('admin');
                    setAdminActiveTab(tab);
                  } else if (isPartner) {
                    setActiveNav('dashboard');
                    setCustomerTab(tab);
                  }
                }}
              />
            </div>
          )}

          {activeNav === '01' && (
            <div className="w-full h-full flex flex-col">
               {/* Header with Title - smaller when content exists */}
               <div className={`text-center w-full animate-in fade-in shrink-0 ${labState.image ? 'pt-2 pb-2' : 'pt-4 sm:pt-8 pb-4'}`}>
                  <h1 className={`font-medium tracking-[-0.08em] uppercase leading-none ${labState.image ? 'text-2xl sm:text-3xl' : 'text-4xl sm:text-5xl md:text-6xl lg:text-7xl'}`} style={{ letterSpacing: '-0.08em' }}>
                    LABOR
                  </h1>
                  <p className={`font-playfair italic text-white/40 mt-1 tracking-wide ${labState.image ? 'text-lg sm:text-xl' : 'text-2xl sm:text-3xl md:text-4xl lg:text-5xl mt-2'}`}>
                    synthese labor
                  </p>
               </div>

               {/* Content Area - grows to fill space */}
               <div className="flex-1 w-full max-w-3xl mx-auto overflow-y-auto aether-scrollbar min-h-0">
                 {/* Chat Messages */}
                 {generationMode === 'chat' && (
                   chatMessages.length === 0 ? (
                     <div className="flex flex-col items-center justify-center h-full text-center py-20">
                       <Send size={32} className="text-white/20 mb-4" />
                       <p className="font-mono text-sm text-white/30">Starte eine Konversation</p>
                       <p className="font-mono text-xs text-white/20 mt-2">Deine Chats werden automatisch in Records gespeichert</p>
                     </div>
                   ) : (
                     <div className="px-4 py-6 space-y-4">
                       {chatMessages.map((msg, idx) => (
                         <motion.div
                           key={idx}
                           initial={{ opacity: 0, y: 10 }}
                           animate={{ opacity: 1, y: 0 }}
                           className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                         >
                           <div className={`max-w-[80%] p-4 rounded-2xl ${
                             msg.role === 'user' 
                               ? 'bg-white/10 text-white ml-auto' 
                               : msg.isError 
                                 ? 'bg-red-500/10 text-red-300 border border-red-500/20' 
                                 : 'monolith-glass text-white/90'
                           }`}>
                             <p className="font-mono text-sm whitespace-pre-wrap">{msg.content}</p>
                             <p className="font-mono text-[9px] text-white/30 mt-2">{msg.timestamp}</p>
                           </div>
                         </motion.div>
                       ))}
                       {chatLoading && (
                         <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                           <div className="monolith-glass p-4 rounded-2xl flex items-center gap-3">
                             <Loader2 size={16} className="animate-spin text-white/50" />
                             <span className="font-mono text-sm text-white/50">Denkt nach...</span>
                           </div>
                         </motion.div>
                       )}
                     </div>
                   )
                 )}

                 {/* Synthesis Content (text, image, both) */}
                 {generationMode !== 'chat' && labState.image && (
                   <motion.div 
                     className="w-full flex flex-col gap-4 px-4 py-4"
                     initial={{ opacity: 0, y: 30 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                   >
                      <motion.div 
                        className="w-full relative aspect-video max-h-[35vh] rounded-2xl overflow-hidden monolith-glass shadow-2xl group aether-card-hover"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        whileHover={{ scale: 1.01 }}
                      >
                         <img 
                           src={labState.image} 
                           onClick={() => setZoomImage(labState.image)} 
                           className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700 cursor-zoom-in" 
                           alt="Vision" 
                         />
                         <div className="absolute bottom-3 right-3 flex gap-2 opacity-100 lg:opacity-0 group-hover:opacity-100 transition-opacity z-10">
                           <button onClick={() => setZoomImage(labState.image)} className="p-2 monolith-heavy hover:bg-white hover:text-black rounded-xl transition-all aether-btn-glow">
                             <Maximize2 size={14}/>
                           </button>
                           <button onClick={() => handleDownloadImage(labState.image, labState.id)} className="p-2 monolith-heavy hover:bg-white hover:text-black rounded-xl transition-all aether-btn-glow">
                             <Download size={14}/>
                           </button>
                           <button onClick={resetLab} className="p-2 monolith-heavy hover:bg-white hover:text-black rounded-xl transition-all text-white/50">
                             <RotateCcw size={14}/>
                           </button>
                         </div>
                      </motion.div>
                      {labState.manifesto && (
                        <motion.div 
                          className="monolith-heavy p-4 sm:p-6 text-center relative border border-white/10 aether-card-hover"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: 0.2 }}
                        >
                          <button 
                            onClick={() => {
                              const e = document.createElement('textarea'); 
                              e.value = safeRender(labState.manifesto); 
                              document.body.appendChild(e); 
                              e.select(); 
                              document.execCommand('copy'); 
                              document.body.removeChild(e); 
                              setCopied(true); 
                              setTimeout(()=>setCopied(false),2000);
                            }} 
                            className="absolute top-4 right-4 text-white/30 hover:text-white"
                          >
                            {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                          </button>
                          <p className="font-playfair italic text-base sm:text-lg text-white/90 leading-relaxed px-4">
                            "{safeRender(labState.manifesto)}"
                          </p>
                        </motion.div>
                      )}
                   </motion.div>
                 )}

                 {/* Empty State for Synthesis */}
                 {generationMode !== 'chat' && !labState.image && (
                   <div className="flex flex-col items-center justify-center h-full text-center py-20">
                     <Sparkles size={32} className="text-white/20 mb-4" />
                     <p className="font-mono text-sm text-white/30">Beschreibe deine Idee</p>
                     <p className="font-mono text-xs text-white/20 mt-2">
                       {generationMode === 'text' ? 'Text-Generierung' : generationMode === 'image' ? 'Bild-Generierung' : 'Text + Bild Synthese'}
                     </p>
                   </div>
                 )}
               </div>

               {/* Bottom Input Area - Fixed */}
               <div className="mt-auto pt-4 w-full max-w-3xl mx-auto">
                 {/* Mode Selector - Above Input */}
                 <div className="flex justify-center mb-3">
                   <div className="flex items-center gap-1 p-1 bg-white/5 rounded-lg">
                     <button
                       onClick={() => setGenerationMode('chat')}
                       className={`px-3 py-1.5 rounded-md font-mono text-[9px] uppercase tracking-widest transition-all flex items-center gap-1.5 ${
                         generationMode === 'chat' ? 'bg-white/10 text-white' : 'text-white/30 hover:text-white/60'
                       }`}
                     >
                       <Send size={12} /> Chat
                     </button>
                     <button
                       onClick={() => setGenerationMode('image')}
                       className={`px-3 py-1.5 rounded-md font-mono text-[9px] uppercase tracking-widest transition-all flex items-center gap-1.5 ${
                         generationMode === 'image' ? 'bg-white/10 text-white' : 'text-white/30 hover:text-white/60'
                       }`}
                     >
                       <ImagePlus size={12} /> Bild
                     </button>
                   </div>
                 </div>

                 {/* Selected Style Badge - UNDER the tabs */}
                 {selectedStyle && generationMode !== 'chat' && (
                   <div className="flex items-center justify-between p-3 mb-3 bg-white/5 rounded-xl border border-white/10">
                     <div className="flex items-center gap-3">
                       <div className="p-2 bg-black/40 rounded-lg">
                         {SelectedStyleIcon && <SelectedStyleIcon size={14} className="text-white/60"/>}
                       </div>
                       <div className="flex flex-col">
                         <span className="font-mono text-[8px] uppercase tracking-widest text-white/40">Basis-Stil</span>
                         <span className="font-mono text-xs text-white">{selectedStyle.name}</span>
                       </div>
                     </div>
                     <button onClick={() => setSelectedStyle(null)} className="text-white/40 hover:text-white p-2">
                       <X size={14} />
                     </button>
                   </div>
                 )}

                 {/* Unified Input Box */}
                 <div className="monolith-glass p-3 shadow-2xl flex flex-col gap-3">
                   <div className="flex items-center gap-3 px-3 py-1">
                     <Shapes size={16} className="text-white/20 shrink-0" />
                     <input 
                       value={prompt} 
                       onChange={e => setPrompt(e.target.value)} 
                       onKeyDown={e => {
                         if (e.key === 'Enter' && !e.shiftKey) {
                           if (generationMode === 'chat') sendChatMessage();
                           else performFullSynthesis();
                         }
                       }}
                       placeholder={generationMode === 'chat' ? "Nachricht schreiben..." : "Idee beschreiben..."} 
                       className="bg-transparent border-none outline-none text-white font-mono text-xs sm:text-sm w-full py-3 placeholder:text-white/20" 
                     />
                     
                     {/* Settings Button */}
                     {generationMode !== 'chat' && (
                       <>
                         {/* Plus Button - Style Presets */}
                         <button 
                           onClick={() => setShowPresetModal(true)} 
                           className="p-2 text-white/30 hover:text-white transition-all shrink-0" 
                           title="Stil wählen"
                         >
                           <Plus size={18} />
                         </button>
                         
                         {/* Lightbulb Button - Inspiration */}
                         <button 
                           onClick={generateInspiration} 
                           disabled={extLoading.inspiration} 
                           className="p-2 text-white/30 hover:text-white transition-all shrink-0" 
                           title="Inspiration"
                         >
                           {extLoading.inspiration ? <Loader2 size={18} className="animate-spin" /> : <Lightbulb size={18} />}
                         </button>
                       </>
                     )}
                     
                     {/* Settings Button - Always visible */}
                     <button 
                       onClick={() => setShowSettingsDropdown(!showSettingsDropdown)} 
                       className={`p-2 transition-all shrink-0 ${showSettingsDropdown ? 'text-white' : 'text-white/30 hover:text-white'}`}
                       title="Einstellungen"
                     >
                       <Settings size={18} />
                     </button>
                   </div>
                   
                   {/* Settings Panel - Expandable */}
                   <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showSettingsDropdown ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                     <div className="px-3 py-3 border-t border-white/5 space-y-3">
                       <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                         <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">KI-Modell</span>
                         <div className="flex items-center gap-1 p-1 bg-white/5 rounded-lg">
                           <button
                             onClick={() => setSelectedModel('gemini')}
                             className={`px-3 sm:px-4 py-1.5 rounded-md font-mono text-[9px] uppercase tracking-widest transition-all ${
                               selectedModel === 'gemini' ? 'bg-white/10 text-white' : 'text-white/30 hover:text-white/60'
                             }`}
                           >
                             Gemini
                           </button>
                           <button
                             onClick={() => setSelectedModel('openai')}
                             className={`px-3 sm:px-4 py-1.5 rounded-md font-mono text-[9px] uppercase tracking-widest transition-all ${
                               selectedModel === 'openai' ? 'bg-white/10 text-white' : 'text-white/30 hover:text-white/60'
                             }`}
                           >
                             GPT
                           </button>
                         </div>
                       </div>
                      {/* Image Format Selection (only for image and synthese modes) */}
                      {(generationMode === 'image' || generationMode === 'both') && (
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">Bild-Format</span>
                          <div className="flex items-center gap-1 p-1 bg-white/5 rounded-lg flex-wrap">
                            {imageFormats.map(format => (
                              <button
                                key={format.id}
                                onClick={() => setImageFormat(format.id)}
                                className={`px-2 sm:px-3 py-1.5 rounded-md font-mono text-[9px] uppercase tracking-widest transition-all ${
                                  imageFormat === format.id ? 'bg-white/10 text-white' : 'text-white/30 hover:text-white/60'
                                }`}
                              >
                                {format.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Negative Prompt (only for image and synthese modes) */}
                      {(generationMode === 'image' || generationMode === 'both') && (
                        <div className="flex flex-col gap-2 pt-2 border-t border-white/5">
                          <button
                            onClick={() => setShowNegativePrompt(!showNegativePrompt)}
                            className="flex items-center justify-between w-full text-left group"
                            data-testid="toggle-negative-prompt"
                          >
                            <span className="font-mono text-[9px] uppercase tracking-widest text-white/40 group-hover:text-white/70 transition-colors">
                              Negativ-Prompt {negativePrompt.trim() && <span className="text-orange-400/70 ml-1">●</span>}
                            </span>
                            <span className="text-white/30 group-hover:text-white/60 transition-colors text-[10px]">
                              {showNegativePrompt ? '−' : '+'}
                            </span>
                          </button>
                          {showNegativePrompt && (
                            <div className="space-y-1.5">
                              <input
                                value={negativePrompt}
                                onChange={(e) => setNegativePrompt(e.target.value)}
                                placeholder="z.B. blurry, low quality, deformed hands, cartoon..."
                                className="w-full bg-white/5 border border-white/5 rounded-lg px-3 py-2 font-mono text-[10px] text-white placeholder:text-white/20 outline-none focus:border-white/20 transition-colors"
                                data-testid="negative-prompt-input"
                              />
                              <p className="font-mono text-[8px] text-white/30 leading-relaxed">
                                Was im Bild nicht erscheinen soll. Standard-Filter (no text, no watermark, no split frame) sind immer aktiv.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                       {/* Clear Chat Button (only in chat mode) */}
                       {generationMode === 'chat' && chatMessages.length > 0 && (
                         <div className="flex gap-2">
                           <button
                             onClick={startNewChat}
                             className="flex-1 py-2 text-white/50 hover:text-white font-mono text-[9px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 monolith-glass rounded-lg"
                           >
                             <Plus size={12} /> Neuer Chat
                           </button>
                           <button
                             onClick={clearChat}
                             className="py-2 px-3 text-white/30 hover:text-red-400 font-mono text-[9px] uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                           >
                             <Trash2 size={12} />
                           </button>
                         </div>
                       )}
                     </div>
                   </div>
                   
                   {/* Action Button */}
                   <button 
                     onClick={() => generationMode === 'chat' ? sendChatMessage() : performFullSynthesis()} 
                     disabled={loading || imageLoading || chatLoading || !prompt.trim()} 
                     className="w-full monolith-heavy py-4 rounded-xl flex items-center justify-center gap-3 hover:bg-white hover:text-black transition-all disabled:opacity-30"
                   >
                     {(loading || imageLoading || chatLoading) ? (
                       <Loader2 size={16} className="animate-spin" />
                     ) : generationMode === 'chat' ? (
                       <Send size={16} />
                     ) : (
                       <Sparkles size={16} />
                     )}
                     <span className="font-mono text-[10px] tracking-[0.4em] uppercase">
                       {loadingPhase || (generationMode === 'chat' ? 'Senden' : 'Synthese starten')}
                     </span>
                   </button>
                 </div>
                 
                 {/* Model Info */}
                 <p className="text-center font-mono text-[9px] text-white/20 mt-2">
                   {selectedModel === 'openai' ? 'GPT-4o' : 'Gemini Flash'} • Enter zum {generationMode === 'chat' ? 'Senden' : 'Starten'}
                 </p>
               </div>
            </div>
          )}

          {activeNav === '04' && (
            <div className="w-full flex flex-col gap-12">
               <div className="mb-4 border-b border-white/10 pb-6">
                 <h2 className="text-3xl font-mono uppercase tracking-widest text-white/80">Chronos Journal</h2>
               </div>
               <div className="relative pl-10">
                 {/* Simple Timeline Line */}
                 <div className="timeline-line" />
                  {journal.length === 0 ? ( 
                    <div className="py-20 text-center opacity-20 flex flex-col items-center gap-4">
                      <Clock size={48} />
                      <p className="font-mono text-xs uppercase tracking-widest">Journal leer.</p>
                    </div> 
                  ) : (
                    <div className="space-y-6">
                      {journal.map((entry, index) => (
                        <div 
                          key={entry.id} 
                          className="relative animate-in slide-in-from-left-4"
                          style={{ animationDelay: `${index * 80}ms`, animationFillMode: 'backwards' }}
                        >
                          {/* Timeline Dot - exactly centered on the line */}
                          <div className="absolute left-[-35px] top-1/2 -translate-y-1/2">
                            <div className="timeline-dot" />
                          </div>
                          {/* Card */}
                          <div className="monolith-glass p-6 hover:bg-white/[0.03] transition-all group relative flex-1 aether-card-hover">
                             <button 
                               onClick={() => {
                                 setJournal(j => j.filter(x => x.id !== entry.id));
                                 deleteJournalFromAPI(entry.id);
                               }} 
                               className="absolute top-4 right-4 text-white/10 hover:text-white transition-colors"
                             >
                               <Trash2 size={14} />
                             </button>
                             <div className="flex items-center gap-4 mb-3 text-white/30 font-mono text-[10px] uppercase tracking-widest">
                               <span><Calendar size={12} className="inline mr-2"/> {entry.date}</span>
                               <span><Clock size={12} className="inline mr-2"/> {entry.time || entry.timestamp}</span>
                             </div>
                             <p className="font-mono text-[11px] text-white/70 leading-relaxed whitespace-pre-wrap">{entry.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
               </div>
            </div>
          )}

          {activeNav === '02' && (
            <div className="w-full flex flex-col gap-8">
               {/* Header without animation */}
               <div className="mb-4 border-b border-white/10 pb-6 flex flex-col gap-4">
                  <h2 className="text-3xl font-mono uppercase tracking-widest text-white/80">Stil-Bibliothek</h2>
                  <div className="monolith-glass flex items-center px-4 py-2 w-full sm:max-w-md border-white/5">
                    <Search size={14} className="text-white/30 mr-3" />
                    <input 
                      value={styleSearchQuery} 
                      onChange={e => setStyleSearchQuery(e.target.value)} 
                      placeholder="Stil suchen..." 
                      className="bg-transparent border-none outline-none font-mono text-[10px] uppercase w-full text-white placeholder:text-white/20" 
                    />
                  </div>
               </div>
               {/* Grid with animations only for cards */}
               <motion.div 
                 className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 pb-24"
                 initial="initial"
                 animate="animate"
                 variants={staggerContainer}
               >
                  {filteredPresets.map((p, index) => {
                    const PresetIcon = p.icon;
                    return (
                      <motion.button 
                        key={p.id} 
                        onClick={() => handleStyleSelect(p)} 
                        className="monolith-glass group flex flex-col border-white/5 hover:border-white/30 transition-all overflow-hidden text-left h-48 relative aether-card-hover"
                        variants={staggerItem}
                        whileHover={{ scale: 1.03, y: -4 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <img src={p.img} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover grayscale opacity-30 group-hover:grayscale-0 group-hover:opacity-60 transition-all duration-700" alt={p.name} />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-transparent" />
                        <div className="relative z-10 p-6 flex flex-col h-full justify-end">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-white/10 rounded-lg text-white">
                              <PresetIcon size={16} />
                            </div>
                            <h3 className="font-mono text-sm uppercase tracking-widest text-white">{p.name}</h3>
                          </div>
                          <p className="font-mono text-[9px] text-white/40 uppercase line-clamp-2">{p.style}</p>
                        </div>
                      </motion.button>
                    );
                  })}
               </motion.div>
            </div>
          )}

          {activeNav === '03' && (
            <div className="w-full flex flex-col gap-8">
               {/* Header without animation */}
               <div className="mb-4 border-b border-white/10 pb-6 flex flex-col gap-4">
                 <h2 className="text-3xl font-mono uppercase tracking-widest text-white/80">Records</h2>
                 <div className="flex flex-col sm:flex-row gap-4">
                   <div className="monolith-glass flex items-center px-4 py-2 flex-1 sm:max-w-md border-white/5">
                     <Search size={14} className="text-white/30 mr-3" />
                     <input 
                       value={searchQuery} 
                       onChange={e => setSearchQuery(e.target.value)} 
                       placeholder="Records suchen..." 
                       className="bg-transparent border-none outline-none font-mono text-[10px] uppercase w-full text-white placeholder:text-white/20" 
                     />
                   </div>
                   {/* Filter Tabs - Only Images and Chats */}
                   <div className="flex items-center gap-1 p-1 monolith-glass rounded-lg">
                     <button
                       onClick={() => setRecordsFilter('image')}
                       className={`px-4 py-2 rounded-md font-mono text-[9px] uppercase tracking-widest transition-all flex items-center gap-1.5 ${
                         recordsFilter === 'image' 
                           ? 'bg-white/10 text-white' 
                           : 'text-white/30 hover:text-white/60'
                       }`}
                     >
                       <ImagePlus size={12} />
                       Bilder
                     </button>
                     <button
                       onClick={() => setRecordsFilter('chat')}
                       className={`px-4 py-2 rounded-md font-mono text-[9px] uppercase tracking-widest transition-all flex items-center gap-1.5 ${
                         recordsFilter === 'chat' 
                           ? 'bg-white/10 text-white' 
                           : 'text-white/30 hover:text-white/60'
                       }`}
                     >
                       <Send size={12} />
                       Chats
                     </button>
                   </div>
                 </div>
               </div>
               
               {filteredHistory.length === 0 ? (
                 <div className="py-20 text-center opacity-20 flex flex-col items-center gap-4">
                   <Archive size={48} />
                   <p className="font-mono text-xs uppercase tracking-widest">Keine Records gefunden.</p>
                 </div>
               ) : (
                 <motion.div 
                   className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-24"
                   initial="initial"
                   animate="animate"
                   variants={staggerContainer}
                 >
                    {filteredHistory.map((item, index) => (
                      <motion.div 
                        key={item.id} 
                        onClick={() => restoreFromHistory(item)} 
                        className="monolith-glass group p-5 flex flex-col gap-4 border-white/5 hover:bg-white/10 cursor-pointer transition-all aether-card-hover"
                        variants={staggerItem}
                        whileHover={{ scale: 1.02, y: -4 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex justify-between items-start text-white/30">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[9px] uppercase">v{safeRender(item.version)}</span>
                            {item.type && (
                              <span className={`font-mono text-[8px] uppercase px-1.5 py-0.5 rounded ${
                                item.type === 'image' || item.type === 'both' ? 'bg-purple-500/20 text-purple-300' :
                                item.type === 'chat' ? 'bg-green-500/20 text-green-300' :
                                'bg-white/10 text-white/50'
                              }`}>
                                {item.type === 'image' || item.type === 'both' ? 'BILD' : item.type === 'chat' ? 'CHAT' : item.type.toUpperCase()}
                              </span>
                            )}
                          </div>
                          <button 
                            onClick={e => {
                              e.stopPropagation(); 
                              setHistory(h => h.filter(x => x.id !== item.id));
                              deleteRecordFromAPI(item.id);
                            }} 
                            className="hover:text-white"
                          >
                            <Trash2 size={14}/>
                          </button>
                        </div>
                        {item.manifesto && (
                          <p className="font-playfair italic text-sm text-white/80 line-clamp-3">"{safeRender(item.manifesto)}"</p>
                        )}
                        {item.type === 'chat' && item.chatMessages && (
                          <div className="space-y-2 mt-2">
                            <p className="font-mono text-[9px] uppercase text-white/40">{item.chatMessages.length} Nachrichten</p>
                            <div className="text-xs text-white/60 line-clamp-2">
                              {item.chatMessages.slice(-2).map((msg, i) => (
                                <span key={i} className={msg.role === 'user' ? 'text-white/40' : 'text-white/70'}>
                                  {msg.role === 'user' ? 'Du: ' : 'AI: '}{msg.content.substring(0, 40)}...{' '}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {item.image && ( 
                          <div className="h-32 rounded-xl overflow-hidden border border-white/5 mt-auto relative group/img">
                            <img src={item.image} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale opacity-50 transition-all" alt="Record" />
                            <div className="absolute bottom-2 right-2 flex gap-1 opacity-100 lg:opacity-0 group-hover/img:opacity-100 transition-opacity z-10">
                              <button onClick={(e) => { e.stopPropagation(); setZoomImage(item.image); }} className="p-2 monolith-heavy rounded-lg transition-all">
                                <Maximize2 size={12} />
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); handleDownloadImage(item.image, item.id); }} className="p-2 monolith-heavy rounded-lg transition-all">
                                <Download size={12} />
                              </button>
                            </div>
                          </div> 
                        )}
                      </motion.div>
                    ))}
                 </motion.div>
               )}
            </div>
          )}

          {/* ========== ADMIN DASHBOARD ========== */}
          {activeNav === 'admin' && isAdminAuthenticated && (() => {
            // Map the current admin sub-tab to a heading + subtitle so the
            // content area always announces which view you're in. (The
            // sub-sidebar is gone — categories live in the main sidebar now.)
            const tabMeta = {
              overview:   { title: 'Übersicht',    sub: 'Stats, Umsatz, Verkäufe, System-Zähler' },
              orders:     { title: 'Bestellungen', sub: 'Alle Käufe, E-Mails erneut senden, archivieren' },
              invoices:   { title: 'Rechnungen',   sub: 'Freie Rechnungen erstellen (z.B. Video-Aufträge) — mit Live-Vorschau' },
              discounts:  { title: 'Rabatte',      sub: 'Rabattcodes erstellen + verwalten' },
              licenses:   { title: 'Lizenzen',     sub: 'Lizenzschlüssel, Aktivierungen pro Gerät' },
              gifts:      { title: 'Verschenken',  sub: 'Ein Produkt kostenlos an eine E-Mail geben — echter Schlüssel, echte Bibliothek' },
              updates:    { title: 'Updates',      sub: 'Neue Plugin-Version bereitstellen + Käufer benachrichtigen' },
              emails:     { title: 'E-Mails',      sub: 'Versendete Mails, Broadcasts, Templates testen' },
              affiliates: { title: 'Affiliates',   sub: 'Affiliate-Partner, Links, Provisionen' },
              users:      { title: 'Nutzer',       sub: 'Portal-Zugänge, Admins + Partner — inkl. Account-Anfragen' },
              logs:       { title: 'Logs',         sub: 'Unveränderliches Audit-Log aller Admin-Aktionen' },
              archive:    { title: 'Archiv',       sub: 'Soft-gelöschte Einträge, Wiederherstellung' },
              gdpr:       { title: 'DSGVO',        sub: 'Auskunft, Löschung, Retention, Backup' },
            };
            const meta = tabMeta[adminActiveTab] || tabMeta.overview;
            return (
            <div className="w-full flex flex-col gap-6">
              {/* Active-tab header — replaces the old "Admin Dashboard" + sub-tab strip */}
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 border-b border-white/10 pb-6">
                <div>
                  <h2 className="text-3xl font-mono uppercase tracking-widest text-white/80">{meta.title}</h2>
                  <p className="text-white/40 text-sm mt-2">{meta.sub}</p>
                </div>
                <button
                  onClick={adminLogout}
                  className="monolith-glass px-4 py-2 flex items-center gap-2 text-white/40 hover:text-white transition-all shrink-0"
                >
                  <LogOut size={14} />
                  <span className="font-mono text-[9px] uppercase tracking-widest">Abmelden</span>
                </button>
              </div>

              {/* Tab content — no inner sidebar anymore. */}
              <div className="flex flex-col gap-6">

              {/* Overview loading / retry fallback — the panel used to render
                  nothing at all while adminStats was still null (initial load
                  OR a failed/ swallowed fetch), leaving the admin on a blank
                  screen with no hint. */}
              {adminActiveTab === 'overview' && !adminStats && (
                <div className="monolith-glass p-12 flex flex-col items-center justify-center text-center gap-3">
                  <Loader2 size={22} className="text-white/30 animate-spin" />
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Kennzahlen werden geladen …</p>
                  <button onClick={fetchAdminData} className="mt-1 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:text-white">
                    Neu laden
                  </button>
                </div>
              )}

              {/* Admin Overview Tab */}
              {adminActiveTab === 'overview' && adminStats && (
                <div className="space-y-6">
                  {/* Cross-link to the full analytics cockpit (Dashboard nav).
                      This panel stays the ops/system snapshot; the richer
                      overview (revenue chart, top products/affiliates, licences,
                      activity) lives in the Dashboard — point at it instead of
                      maintaining a competing half-copy. */}
                  <button
                    onClick={() => setActiveNav('dashboard')}
                    className="w-full monolith-glass p-4 flex items-center justify-between gap-3 text-left hover:bg-white/[0.03] transition group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2.5 rounded-xl bg-orange-400/10 shrink-0">
                        <TrendingUp size={18} className="text-orange-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-white">Volles Cockpit im Dashboard</p>
                        <p className="text-xs text-white/50 truncate">Umsatz-Verlauf, Top-Produkte, Top-Affiliates, Lizenzen &amp; Aktivität</p>
                      </div>
                    </div>
                    <ChevronRight size={18} className="text-white/30 group-hover:text-white/60 group-hover:translate-x-1 transition shrink-0" />
                  </button>

                  {/* Headline stats */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="monolith-glass p-6">
                      <div className="flex items-center gap-3 mb-3 text-white/40">
                        <DollarSign size={16} />
                        <span className="font-mono text-[9px] uppercase tracking-widest">Umsatz</span>
                      </div>
                      <p className="text-2xl font-mono text-white">{adminStats.total_revenue?.toFixed(2) || '0.00'} €</p>
                    </div>
                    <div className="monolith-glass p-6">
                      <div className="flex items-center gap-3 mb-3 text-white/40">
                        <Package size={16} />
                        <span className="font-mono text-[9px] uppercase tracking-widest">Verkäufe</span>
                      </div>
                      <p className="text-2xl font-mono text-white">{adminStats.total_sales || 0}</p>
                    </div>
                    <div className="monolith-glass p-6">
                      <div className="flex items-center gap-3 mb-3 text-white/40">
                        <Users size={16} />
                        <span className="font-mono text-[9px] uppercase tracking-widest">Affiliates</span>
                      </div>
                      <p className="text-2xl font-mono text-white">{adminStats.total_affiliates || 0}</p>
                    </div>
                    <div className="monolith-glass p-6">
                      <div className="flex items-center gap-3 mb-3 text-white/40">
                        <Percent size={16} />
                        <span className="font-mono text-[9px] uppercase tracking-widest">Provision</span>
                      </div>
                      <p className="text-2xl font-mono text-white">{adminStats.total_commissions?.toFixed(2) || '0.00'} €</p>
                    </div>
                  </div>

                  {/* Revenue windows */}
                  <div>
                    <h3 className="font-mono text-[9px] uppercase tracking-widest text-white/30 mb-3">Zeitfenster</h3>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="monolith-glass p-5">
                        <div className="flex items-center gap-2 mb-2 text-white/40">
                          <Clock size={14} />
                          <span className="font-mono text-[9px] uppercase tracking-widest">Heute</span>
                        </div>
                        <p className="text-xl font-mono text-white">{(adminStats.revenue_today || 0).toFixed(2)} €</p>
                        <p className="text-white/40 font-mono text-[10px] mt-1">{adminStats.sales_today || 0} Verkäufe</p>
                      </div>
                      <div className="monolith-glass p-5">
                        <div className="flex items-center gap-2 mb-2 text-white/40">
                          <Clock size={14} />
                          <span className="font-mono text-[9px] uppercase tracking-widest">Letzte 7 Tage</span>
                        </div>
                        <p className="text-xl font-mono text-white">{(adminStats.revenue_week || 0).toFixed(2)} €</p>
                        <p className="text-white/40 font-mono text-[10px] mt-1">{adminStats.sales_week || 0} Verkäufe</p>
                      </div>
                      <div className="monolith-glass p-5">
                        <div className="flex items-center gap-2 mb-2 text-white/40">
                          <Calendar size={14} />
                          <span className="font-mono text-[9px] uppercase tracking-widest">Letzte 30 Tage</span>
                        </div>
                        <p className="text-xl font-mono text-white">{(adminStats.revenue_month || 0).toFixed(2)} €</p>
                        <p className="text-white/40 font-mono text-[10px] mt-1">{adminStats.sales_month || 0} Verkäufe</p>
                      </div>
                    </div>
                  </div>

                  {/* System counts */}
                  <div>
                    <h3 className="font-mono text-[9px] uppercase tracking-widest text-white/30 mb-3">System</h3>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="monolith-glass p-5">
                        <div className="flex items-center gap-2 mb-2 text-white/40">
                          <User size={14} />
                          <span className="font-mono text-[9px] uppercase tracking-widest">Unique Kunden</span>
                        </div>
                        <p className="text-xl font-mono text-white">{adminStats.unique_customers || 0}</p>
                      </div>
                      <div className="monolith-glass p-5">
                        <div className="flex items-center gap-2 mb-2 text-white/40">
                          <Mail size={14} />
                          <span className="font-mono text-[9px] uppercase tracking-widest">E-Mails geloggt</span>
                        </div>
                        <p className="text-xl font-mono text-white">{adminStats.email_log_count || 0}</p>
                      </div>
                      <div className="monolith-glass p-5">
                        <div className="flex items-center gap-2 mb-2 text-white/40">
                          <Activity size={14} />
                          <span className="font-mono text-[9px] uppercase tracking-widest">Activity-Einträge</span>
                        </div>
                        <p className="text-xl font-mono text-white">{adminStats.activity_count || 0}</p>
                      </div>
                      <div className="monolith-glass p-5">
                        <div className="flex items-center gap-2 mb-2 text-white/40">
                          <Archive size={14} />
                          <span className="font-mono text-[9px] uppercase tracking-widest">Archiv</span>
                        </div>
                        <p className="text-xl font-mono text-white">{adminStats.archive_count || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Anfragen aus dem Kontaktformular — eigene Datei, holt sich die
                  Liste selbst und nutzt dasselbe AdminListView wie unten. */}
              {adminActiveTab === 'requests' && (
                <RequestsView authFetch={authFetch} />
              )}

              {/* Admin Orders Tab — refactored onto AdminListView (unified
                  filter bar + cog with row size / column visibility / group). */}
              {adminActiveTab === 'orders' && (
                <AdminListView
                  storageKey="orders"
                  data={filteredOrders}
                  rowKey={(o) => o.session_id}
                  searchValue={orderSearch}
                  onSearchChange={setOrderSearch}
                  searchPlaceholder="Bestellung suchen (Nummer, E-Mail, Lizenz, Produkt…)"
                  filters={[
                    {
                      id: 'product',
                      label: 'Produkt',
                      value: orderProductFilter,
                      onChange: setOrderProductFilter,
                      options: [
                        { value: 'all', label: 'Alle Produkte' },
                        ...ADMIN_PRODUCTS.filter(p => p.id).map(p => ({ value: p.id, label: p.name })),
                      ],
                    },
                    {
                      id: 'date',
                      label: 'Zeitraum',
                      value: orderDateFilter,
                      onChange: setOrderDateFilter,
                      options: [
                        { value: 'all',   label: 'Alle Zeit' },
                        { value: 'today', label: 'Heute' },
                        { value: 'week',  label: 'Diese Woche' },
                        { value: 'month', label: 'Dieser Monat' },
                      ],
                    },
                  ]}
                  groupByStatus
                  groupKey={(o) => o.payment_status || '—'}
                  groupLabels={{ paid: 'Bezahlt', free: 'Kostenlos', pending: 'Ausstehend' }}
                  emptyText="Keine Bestellungen"
                  emptyIcon={Package}
                  columns={[
                    {
                      id: 'product',
                      label: 'Produkt',
                      locked: true,
                      render: (o) => (
                        <div className="flex items-center gap-2">
                          <span className="text-white">{o.product_name}</span>
                          <span className={`px-2 py-0.5 rounded text-[8px] font-mono uppercase shrink-0 ${
                            o.payment_status === 'paid'
                              ? 'bg-green-500/20 text-green-300'
                              : o.payment_status === 'free'
                                ? 'bg-blue-500/20 text-blue-300'
                                : 'bg-yellow-500/20 text-yellow-300'
                          }`}>{o.payment_status}</span>
                        </div>
                      ),
                    },
                    { id: 'order_number', label: 'Order #', mono: true, muted: true, render: (o) => o.order_number || '—' },
                    { id: 'customer',     label: 'Kunde',  mono: true, render: (o) => o.customer_email || '—' },
                    {
                      id: 'amount',
                      label: 'Betrag',
                      align: 'right',
                      render: (o) => (
                        <span className="font-mono text-white">{o.amount_total?.toFixed(2) ?? '0.00'} €</span>
                      ),
                    },
                    {
                      id: 'license',
                      label: 'Lizenz',
                      mono: true,
                      muted: true,
                      render: (o) => o.license_key ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); copyAdminToClipboard(o.license_key, o.session_id); }}
                          className="flex items-center gap-1 hover:text-white"
                          title="Lizenz kopieren"
                        >
                          <Key size={10} />
                          <span className="truncate max-w-[160px]">
                            {adminCopiedCode === o.session_id ? 'Kopiert!' : o.license_key}
                          </span>
                        </button>
                      ) : '—',
                    },
                    {
                      id: 'date',
                      label: 'Datum',
                      mono: true,
                      muted: true,
                      defaultVisible: true,
                      render: (o) => o.created_at ? new Date(o.created_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—',
                    },
                  ]}
                  rowActions={(o) => (
                    <>
                      {(o.amount_total || 0) > 0 && (
                        <button
                          onClick={() => openInvoice(
                            { product_name: o.product_name, order_number: o.order_number, amount: o.amount_total, currency: o.currency, created_at: o.created_at },
                            { name: o.customer_name, email: o.customer_email, customer_number: o.customer_number },
                          )}
                          className="p-1.5 text-white/40 hover:text-white transition-colors"
                          title="Rechnung als PDF"
                        >
                          <FileDown size={13} />
                        </button>
                      )}
                      <button
                        onClick={() => resendEmail(o.session_id)}
                        disabled={resendingEmail === o.session_id}
                        className="p-1.5 text-white/40 hover:text-white transition-colors"
                        title="E-Mail erneut senden"
                      >
                        {resendingEmail === o.session_id ? (
                          <Loader2 size={13} className="animate-spin" />
                        ) : resendSuccess === o.session_id ? (
                          <Check size={13} className="text-green-400" />
                        ) : (
                          <Mail size={13} />
                        )}
                      </button>
                      <button
                        onClick={() => deleteOrder(o.session_id)}
                        className="p-1.5 text-white/20 hover:text-red-400 transition-colors"
                        title="Bestellung archivieren"
                      >
                        <Trash2 size={13} />
                      </button>
                    </>
                  )}
                />
              )}

              {/* Admin Updates Tab — TWO STEPS, intentionally separated:
                  Step 1 saves the version + download URL. Customers can
                  immediately pull it from their library, but nobody gets
                  emailed yet — you can quietly stage new versions, test
                  them on the shop, fix typos, whatever.
                  Step 2 is a separate button that blasts the announcement
                  email to every paying customer of that product, using
                  whatever version is currently saved. */}
              {/* Rundmail an Kaeufer. Frueher ein eigener Reiter "Updates" — die
                  Extensions aktualisieren sich inzwischen ueber den Installer,
                  uebrig bleibt das Verschicken. Das gehoert zu den E-Mails.
                  Schritt 1 (Version speichern) bleibt, weil Shop und
                  Kundenbibliothek ihre Versionsangabe und den Download-Link
                  daher beziehen. */}
              {adminActiveTab === 'emails' && (
                <div className="flex gap-1 border-b mb-5" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                  {[
                    { id: 'senden',    label: 'Rundmail an Käufer' },
                    { id: 'protokoll', label: 'E-Mail-Aktivität' },
                  ].map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setMailTab(t.id)}
                      className={`px-4 py-2.5 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px transition ${
                        mailTab === t.id ? 'border-orange-500 text-white' : 'border-transparent text-white/40 hover:text-white/70'
                      }`}
                      data-testid={`aether-mail-tab-${t.id}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}

              {adminActiveTab === 'emails' && mailTab === 'senden' && (
                <div className="space-y-6 max-w-5xl">
                  <div>
                    <h3 className="font-mono text-xs uppercase tracking-widest text-white/40 mb-1">Rundmail an Käufer</h3>
                    <p className="text-white/60 text-sm">
                      In zwei Schritten: zuerst die neue Version speichern
                      (live im Shop und im Kundenkonto, aber keine Mails).
                      Wenn alles bereit ist, separat allen Käufern Bescheid geben.
                    </p>
                  </div>

                  {/* Product */}
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">
                      Produkt
                    </label>
                    <select
                      value={updateForm.product_id}
                      onChange={(e) => setUpdateForm((f) => ({ ...f, product_id: e.target.value }))}
                      className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-white font-mono text-sm outline-none focus:border-white/30"
                      data-testid="update-push-product"
                    >
                      <option value="studio-flow">Studio Flow</option>
                      <option value="instant-curve">Instant Curve</option>
                      <option value="media-flow">Media Flow</option>
                      <option value="instant-grade">Instant Grade</option>
                      <option value="effect-flow">Effect Flow</option>
                      <option value="sfxgrid">SFX Grid</option>
                      <option value="twirl-background">Twirl Background</option>
                      <option value="3d-shape-array">3D Shape Array</option>
                      <option value="2d-carousel">2D Carousel</option>
                      <option value="text-animations">Text Animations</option>
                    </select>
                  </div>

                  {/* STEP 1: save version --------------------------- */}
                  <div className="border border-white/10 rounded-xl p-5 space-y-4">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/10 font-mono text-[10px] text-white/70">1</span>
                      <h4 className="font-mono text-xs uppercase tracking-widest text-white/70">Version speichern</h4>
                      <span className="font-mono text-[10px] text-white/40">— keine Mails</span>
                    </div>

                    {/* Version */}
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">
                        Version
                      </label>
                      <input
                        type="text"
                        value={updateForm.version}
                        onChange={(e) => setUpdateForm((f) => ({ ...f, version: e.target.value }))}
                        placeholder="z. B. 1.3.0"
                        className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-white font-mono text-sm outline-none focus:border-white/30"
                        data-testid="update-push-version"
                      />
                    </div>

                    {/* Download URL */}
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">
                        Download-URL
                      </label>
                      <input
                        type="url"
                        value={updateForm.download_url}
                        onChange={(e) => setUpdateForm((f) => ({ ...f, download_url: e.target.value }))}
                        placeholder="https://www.davidmedia.de/downloads/GraphFlow_v1.3.0.zxp"
                        className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-white font-mono text-xs outline-none focus:border-white/30"
                        data-testid="update-push-url"
                      />
                      <p className="text-white/40 text-xs mt-1 font-mono">
                        Beim Mail-Klick wird hierhin per HTTP 302 redirected.
                      </p>
                    </div>

                    {/* Changelog */}
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">
                        Changelog (optional, erscheint später in der Mail)
                      </label>
                      <textarea
                        value={updateForm.changelog}
                        onChange={(e) => setUpdateForm((f) => ({ ...f, changelog: e.target.value }))}
                        placeholder="• Neu: ...&#10;• Fix: ...&#10;• Performance: ..."
                        rows={5}
                        className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-white font-mono text-xs outline-none focus:border-white/30 resize-y"
                        data-testid="update-push-changelog"
                      />
                    </div>

                    <button
                      onClick={handleSaveVersion}
                      disabled={
                        updateSaving ||
                        updateNotifying ||
                        !updateForm.version.trim() ||
                        !updateForm.download_url.trim()
                      }
                      className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 disabled:opacity-50 disabled:cursor-not-allowed transition text-white font-medium px-4 py-3 rounded-lg"
                      data-testid="update-save-version"
                    >
                      <CheckCircle2 size={16} />
                      {updateSaving ? 'Wird gespeichert ...' : 'Version speichern'}
                    </button>
                  </div>

                  {/* STEP 2: notify customers ----------------------- */}
                  <div className="border border-orange-500/30 rounded-xl p-5 space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-500/20 font-mono text-[10px] text-orange-300">2</span>
                      <h4 className="font-mono text-xs uppercase tracking-widest text-orange-300">Käufer benachrichtigen</h4>
                    </div>
                    <p className="text-white/60 text-sm">
                      Schickt allen bestehenden Käufern dieses Produkts eine
                      Mail mit personalisiertem Download-Link
                      <strong> der aktuell gespeicherten Version </strong>
                      — kein Login nötig. Erst klicken, wenn alles getestet
                      und ready ist.
                    </p>

                    <button
                      onClick={handleNotifyCustomers}
                      disabled={updateNotifying || updateSaving}
                      className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed transition text-black font-bold px-4 py-3 rounded-lg"
                      data-testid="update-notify-customers"
                    >
                      <Rocket size={16} />
                      {updateNotifying ? 'Mails werden verschickt ...' : 'Käufer jetzt benachrichtigen'}
                    </button>
                  </div>

                  {/* Result panel ----------------------------------- */}
                  {updateResult && (
                    <div
                      className={`flex gap-3 p-4 rounded-lg border ${
                        updateResult.ok
                          ? 'border-green-500/30 bg-green-500/5'
                          : 'border-red-500/30 bg-red-500/5'
                      }`}
                      data-testid="update-push-result"
                    >
                      {updateResult.ok ? (
                        <CheckCircle2 size={18} className="text-green-400 shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
                      )}
                      <div className="text-sm">
                        {updateResult.ok ? (
                          updateResult.kind === 'saved' ? (
                            <p className="text-green-300">
                              Version <strong>v{updateResult.version}</strong> gespeichert.
                              Kunden sehen sie ab jetzt in ihrem Konto und im
                              Installer. Keine Mails verschickt.
                            </p>
                          ) : (
                            <p className="text-green-300">
                              <strong>{updateResult.sent}</strong> Mail(s) an Käufer
                              von v{updateResult.version} verschickt
                              {updateResult.failed > 0 ? (
                                <> · <span className="text-orange-300">{updateResult.failed} fehlgeschlagen</span></>
                              ) : null}
                              {' '}({updateResult.total_customers} Kunde
                              {updateResult.total_customers === 1 ? '' : 'n'} insgesamt).
                            </p>
                          )
                        ) : (
                          <p className="text-red-300">Fehler: {updateResult.error}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Admin Email Log Tab */}
              {adminActiveTab === 'emails' && mailTab === 'protokoll' && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h3 className="font-mono text-xs uppercase tracking-widest text-white/40 mb-1">E-Mail-Aktivität</h3>
                      <p className="text-white/60 text-sm">{emailLog.length} Versendungen geloggt — neueste zuerst.</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        type="text"
                        value={emailSearch}
                        onChange={(e) => setEmailSearch(e.target.value)}
                        placeholder="Suchen (E-Mail, Produkt, Key, Order…)"
                        className="px-3 py-2 rounded-lg text-sm bg-white/5 outline-none focus:ring-1 focus:ring-orange-500/50 w-72 max-w-full"
                        style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)' }}
                      />
                      <button
                        onClick={() => { setBroadcastResult(null); setBroadcastOpen(true); }}
                        className="px-3 py-2 rounded-lg text-xs font-mono uppercase tracking-widest text-white liquid-glass hover:bg-white/10 flex items-center gap-2"
                      >
                        <Send size={14} /> Broadcast
                      </button>
                      {/* Test-Send: shoots all 10 template variants at the entered
                          email so you can preview every outgoing mail end-to-end. */}
                      <button
                        onClick={async () => {
                          const target = window.prompt(
                            'An welche E-Mail sollen alle 10 Templates als Vorschau geschickt werden?',
                            'davidferfers2023@gmail.com'
                          );
                          if (!target) return;
                          setEmailPreviewBusy(true);
                          setEmailPreviewResult(null);
                          try {
                            const res = await authFetch(`/api/admin/email-preview/send-all?email=${encodeURIComponent(target)}`, { method: 'POST' });
                            const data = await res.json();
                            if (!res.ok) throw new Error(data.detail || 'Fehler');
                            setEmailPreviewResult(data);
                            fetchAdminData();
                          } catch (e) {
                            setEmailPreviewResult({ error: e.message });
                          } finally {
                            setEmailPreviewBusy(false);
                          }
                        }}
                        disabled={emailPreviewBusy}
                        className="px-3 py-2 rounded-lg text-xs font-mono uppercase tracking-widest text-white liquid-glass hover:bg-white/10 flex items-center gap-2 disabled:opacity-40"
                        title="Alle 10 Templates als Vorschau an eine E-Mail-Adresse senden"
                      >
                        <Mail size={14} /> {emailPreviewBusy ? 'Sendet…' : 'Alle 10 testen'}
                      </button>
                      <button
                        onClick={fetchAdminData}
                        className="px-3 py-2 rounded-lg text-xs font-medium text-white/70 hover:text-white liquid-glass hover:bg-white/10"
                      >
                        ↻
                      </button>
                    </div>
                  </div>

                  {/* Result banner for 'Alle 6 testen' */}
                  {emailPreviewResult && (
                    <div className={`p-3 rounded-lg text-xs font-mono ${emailPreviewResult.error ? 'bg-red-500/10 text-red-300' : 'bg-emerald-500/10 text-emerald-300'}`}>
                      {emailPreviewResult.error ? (
                        `Fehler: ${emailPreviewResult.error}`
                      ) : (
                        <>
                          <p className="mb-1">
                            {emailPreviewResult.ok_count}/{emailPreviewResult.total} Templates an {emailPreviewResult.target} gesendet:
                          </p>
                          <ul className="space-y-0.5 mt-2">
                            {emailPreviewResult.results.map((r, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <span className={r.ok ? 'text-emerald-400' : 'text-red-400'}>{r.ok ? '✓' : '✗'}</span>
                                <span>{r.template}</span>
                                {r.error && <span className="text-red-300/70 text-[10px]">— {r.error}</span>}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  )}

                  <AdminListView
                    storageKey="emails"
                    data={emailLog.filter(e =>
                      emailStatusFilter === 'all' ? true
                      : emailStatusFilter === 'sent' ? !!e.success
                      : !e.success
                    )}
                    rowKey={(e) => e.id || `${e.to}-${e.sent_at}`}
                    searchValue={emailSearch}
                    onSearchChange={setEmailSearch}
                    searchPlaceholder="Suchen (E-Mail, Produkt, Key, Order…)"
                    searchFields={['to', 'product_id', 'product_name', 'license_key', 'order_number', 'subject']}
                    emptyText="Noch keine E-Mails geloggt"
                    emptyIcon={Mail}
                    filters={[{
                      id: 'status', label: 'Status',
                      value: emailStatusFilter, onChange: setEmailStatusFilter,
                      options: [
                        { value: 'all', label: 'Alle Status' },
                        { value: 'sent', label: 'Nur versendet' },
                        { value: 'failed', label: 'Nur Fehler' },
                      ],
                    }]}
                    columns={[
                      { id: 'sent_at', label: 'Wann', mono: true, render: (e) => e.sent_at ? new Date(e.sent_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—' },
                      { id: 'to', label: 'An', render: (e) => <span title={e.to}>{e.to || '—'}</span> },
                      { id: 'product', label: 'Produkt', render: (e) => e.product_name || e.product_id || '—' },
                      { id: 'order_number', label: 'Order', mono: true, muted: true, render: (e) => e.order_number || '—' },
                      { id: 'license_key', label: 'Lizenz', mono: true, muted: true, defaultVisible: false, render: (e) => e.license_key || '—' },
                      { id: 'had_download_link', label: 'Datei', defaultVisible: false, render: (e) => e.had_download_link
                          ? <span className="text-green-400">mit Link</span>
                          : <span className="text-orange-400">ohne (Pre-Release)</span> },
                      { id: 'status', label: 'Status', locked: true, render: (e) => e.success
                          ? <span className="px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 text-[10px]">Versendet</span>
                          : <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 text-[10px]" title={e.error}>Fehler</span> },
                    ]}
                    rowActions={(e) => e.id && (
                      <button onClick={() => deleteEmailLog(e.id)} className="p-1.5 text-white/20 hover:text-red-400">
                        <Trash2 size={12} />
                      </button>
                    )}
                  />
                </div>
              )}

              {/* ========== Admin Logs Tab (immutable activity log) ========== */}
              {adminActiveTab === 'logs' && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h3 className="font-mono text-xs uppercase tracking-widest text-white/40 mb-1">Activity-Log</h3>
                      <p className="text-white/60 text-sm">{activityLog.length} Einträge — read-only. Was wer wann gemacht hat.</p>
                    </div>
                    <button
                      onClick={fetchAdminData}
                      className="px-3 py-2 rounded-lg text-xs font-medium text-white/70 hover:text-white liquid-glass hover:bg-white/10"
                    >↻</button>
                  </div>

                  <AdminListView
                    storageKey="logs"
                    data={activityLog}
                    rowKey={(e) => e.id || `${e.at}-${e.action}`}
                    searchValue={activitySearch}
                    onSearchChange={setActivitySearch}
                    searchPlaceholder="Suchen (Action, Target, Actor…)"
                    searchFields={['action', 'target_type', 'target_id', 'actor']}
                    emptyText="Noch keine Aktivität geloggt"
                    emptyIcon={Activity}
                    columns={[
                      { id: 'at', label: 'Wann', mono: true, render: (e) => e.at ? new Date(e.at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—' },
                      { id: 'actor', label: 'Wer', render: (e) => e.actor || 'system' },
                      { id: 'action', label: 'Aktion', locked: true, render: (e) =>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-white/10 text-white/80">{e.action}</span>
                      },
                      { id: 'target', label: 'Ziel', mono: true, muted: true, render: (e) =>
                          <>{e.target_type || '—'}{e.target_id ? <span className="text-white/30"> · {String(e.target_id).slice(0, 16)}</span> : null}</>
                      },
                      { id: 'details', label: 'Details', mono: true, muted: true, defaultVisible: false, render: (e) =>
                          (e.details && Object.keys(e.details).length
                            ? <span className="text-[10px] whitespace-pre-wrap break-all">{Object.entries(e.details).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join('  ·  ')}</span>
                            : '—')
                      },
                    ]}
                  />
                  <p className="text-white/30 text-[10px] font-mono">
                    Hinweis: Dieses Log ist <span className="text-white/60">unveränderlich</span> — es gibt keinen Lösch-Button, das ist Absicht.
                  </p>
                </div>
              )}

              {/* ========== Admin Archive Tab (soft-deleted, restorable) ========== */}
              {adminActiveTab === 'archive' && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h3 className="font-mono text-xs uppercase tracking-widest text-white/40 mb-1">Archiv</h3>
                      <p className="text-white/60 text-sm">{archiveItems.length} archivierte Einträge — alles was gelöscht wurde, lässt sich hier wiederherstellen.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={archiveKindFilter}
                        onChange={(e) => setArchiveKindFilter(e.target.value)}
                        className="monolith-glass px-3 py-2 font-mono text-[10px] text-white outline-none cursor-pointer"
                        style={darkSelectStyle}
                      >
                        <option value="all" style={darkOptionStyle}>Alle Typen</option>
                        <option value="affiliate" style={darkOptionStyle}>Affiliates</option>
                        <option value="discount_code" style={darkOptionStyle}>Rabattcodes</option>
                        <option value="order" style={darkOptionStyle}>Bestellungen</option>
                        <option value="email" style={darkOptionStyle}>E-Mails</option>
                        <option value="license" style={darkOptionStyle}>Lizenzen</option>
                      </select>
                      <button
                        onClick={fetchAdminData}
                        className="px-3 py-2 rounded-lg text-xs font-medium text-white/70 hover:text-white liquid-glass hover:bg-white/10"
                      >
                        ↻
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {archiveItems
                      .filter((a) => archiveKindFilter === 'all' || a.kind === archiveKindFilter)
                      .map((entry) => {
                        const p = entry.payload || {};
                        // Build a one-line label that's meaningful per kind
                        let label = entry.original_id || '—';
                        if (entry.kind === 'affiliate')      label = `${p.name || '—'}  ·  ${p.email || ''}  ·  ${p.code || ''}`;
                        else if (entry.kind === 'discount_code') label = `${p.code || '—'}  ·  ${p.discount_percent || 0}%`;
                        else if (entry.kind === 'order')     label = `${p.product_name || p.product_id || '—'}  ·  ${p.customer_email || ''}  ·  ${(p.amount ?? '').toString()} ${p.currency || ''}`;
                        else if (entry.kind === 'email')     label = `${p.subject || ''}  ·  ${p.to || ''}`;
                        else if (entry.kind === 'license')   label = `${p.key || '—'}  ·  ${p.products || ''}  ·  ${p.key_type || ''}`;
                        return (
                          <div key={entry.archive_id} className="monolith-glass p-4 flex flex-wrap items-center justify-between gap-3">
                            <div className="flex-1 min-w-[240px]">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="px-2 py-0.5 rounded-full text-[9px] font-mono uppercase bg-white/10 text-white/70">{entry.kind}</span>
                                <span className="text-white/30 text-[10px] font-mono">archiviert {entry.archived_at ? new Date(entry.archived_at).toLocaleString('de-DE') : '—'} von {entry.archived_by || 'system'}</span>
                              </div>
                              <p className="font-mono text-xs text-white/90 truncate" title={label}>{label}</p>
                              {entry.reason && <p className="font-mono text-[10px] text-white/30 mt-1">Grund: {entry.reason}</p>}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => restoreArchived(entry.archive_id, label)}
                                className="monolith-heavy px-3 py-1.5 flex items-center gap-2 text-white/60 hover:text-white text-[9px] font-mono uppercase tracking-widest"
                              >
                                <RotateCcw size={12} /> Wiederherstellen
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    {archiveItems.filter((a) => archiveKindFilter === 'all' || a.kind === archiveKindFilter).length === 0 && (
                      <div className="py-12 text-center text-white/30">
                        <Archive size={32} className="mx-auto mb-3 opacity-50" />
                        <p className="font-mono text-[10px] uppercase tracking-widest">Archiv ist leer</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Admin Affiliates Tab */}
              {adminActiveTab === 'affiliates' && selectedAffiliateId && (
                <AffiliateDetail
                  affiliateId={selectedAffiliateId}
                  onBack={() => setSelectedAffiliateId(null)}
                  onOpenUser={() => { setSelectedAffiliateId(null); setAdminActiveTab('users'); }}
                />
              )}

              {adminActiveTab === 'affiliates' && !selectedAffiliateId && (
                <div className="space-y-6">
                  {/* Affiliates are now created via Nutzerverwaltung — every
                      partner/admin user gets a tracking code automatically.
                      Standalone affiliate-without-user is no longer allowed. */}
                  <div className="p-4 rounded-xl flex items-start gap-3" style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)' }}>
                    <div className="shrink-0 mt-0.5 text-orange-400" style={{ fontSize: 14 }}>ℹ︎</div>
                    <div className="text-xs leading-relaxed text-white/70 flex-1">
                      <p className="text-white mb-1">Affiliates werden über die Nutzerverwaltung angelegt.</p>
                      <p>
                        Jeder Nutzer mit Rolle <strong className="text-white">Partner</strong> oder <strong className="text-white">Admin</strong> bekommt automatisch einen Affiliate-Code. So ist jeder Affiliate immer mit einem echten Login verknüpft.
                      </p>
                      <button
                        onClick={() => { setActiveNav('admin'); setAdminActiveTab('users'); }}
                        className="mt-3 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:text-white transition"
                      >
                        → Zur Nutzerverwaltung
                      </button>
                    </div>
                  </div>

                  <AdminListView
                    storageKey="affiliates"
                    data={affiliates.filter(a =>
                      affiliateActiveFilter === 'all' ? true
                      : affiliateActiveFilter === 'active' ? (a.is_active !== false)
                      : a.is_active === false
                    )}
                    rowKey={(a) => a.id || a.code}
                    searchValue={affiliateSearch}
                    onSearchChange={setAffiliateSearch}
                    searchPlaceholder="Affiliate suchen..."
                    searchFields={['name', 'email', 'code']}
                    emptyText="Noch keine Affiliates"
                    emptyIcon={LinkIcon}
                    onRowClick={(a) => setSelectedAffiliateId(a.id)}
                    filters={[{
                      id: 'active', label: 'Status',
                      value: affiliateActiveFilter, onChange: setAffiliateActiveFilter,
                      options: [
                        { value: 'all', label: 'Alle' },
                        { value: 'active', label: 'Aktiv' },
                        { value: 'inactive', label: 'Inaktiv' },
                      ],
                    }]}
                    columns={[
                      { id: 'name', label: 'Name', locked: true, render: (a) => (
                          <div>
                            <p className="text-white truncate">{a.name || a.email}</p>
                            <p className="font-mono text-[10px] text-white/40 truncate">{a.email}</p>
                          </div>
                        ) },
                      { id: 'code', label: 'Code', render: (a) => (
                          <div className="flex items-center gap-2">
                            <code className="font-mono text-[10px] text-orange-300 bg-orange-300/10 px-2 py-0.5 rounded">{a.code}</code>
                            <button
                              onClick={(e) => { e.stopPropagation(); copyAdminToClipboard(`${window.location.origin}?ref=${a.code}`, a.code); }}
                              className="text-white/30 hover:text-white"
                              title="Affiliate-Link kopieren"
                            >
                              {adminCopiedCode === a.code ? <Check size={12} className="text-green-400" /> : <LinkIcon size={12} />}
                            </button>
                          </div>
                        ) },
                      { id: 'rate', label: 'Provision', mono: true, defaultVisible: false, render: (a) => `${Math.round((a.commission_rate || 0) * 100)}%` },
                      { id: 'sales', label: 'Verkäufe', mono: true, align: 'right', render: (a) => a.total_sales || 0 },
                      { id: 'commission', label: 'Gesamtprov.', mono: true, align: 'right', render: (a) => `${(a.total_commission || 0).toFixed(2)} €` },
                    ]}
                    rowActions={(a) => (
                      <>
                        <ChevronRight size={12} className="text-white/30 self-center" />
                        <button onClick={() => deleteAffiliate(a.id)} className="p-1.5 text-white/20 hover:text-red-400" title="Affiliate löschen">
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  />
                </div>
              )}

              {/* Admin Discounts Tab */}
              {adminActiveTab === 'discounts' && (
                <div className="space-y-6">
                  {/* New Discount Form */}
                  <form onSubmit={createDiscountCode} className="monolith-glass p-5">
                    <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-4">Neuer Rabattcode</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      <input
                        value={newDiscount.code}
                        onChange={e => setNewDiscount({ ...newDiscount, code: e.target.value.toUpperCase() })}
                        placeholder="CODE"
                        required
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-[11px] outline-none focus:border-white/30 uppercase"
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={newDiscount.discount_percent}
                          onChange={e => setNewDiscount({ ...newDiscount, discount_percent: parseInt(e.target.value) || 0 })}
                          min="1"
                          max="100"
                          required
                          className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-[11px] outline-none focus:border-white/30"
                        />
                        <span className="text-white/40 font-mono">%</span>
                      </div>
                      <select
                        value={newDiscount.scope}
                        onChange={e => setNewDiscount({ ...newDiscount, scope: e.target.value })}
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-[11px] outline-none cursor-pointer"
                        style={darkSelectStyle}
                      >
                        {DISCOUNT_SCOPES.map(s => (
                          <option key={s.id} value={s.id} style={darkOptionStyle}>{s.name}</option>
                        ))}
                      </select>
                      {newDiscount.scope === 'specific_product' && (
                        <select
                          value={newDiscount.product_id}
                          onChange={e => setNewDiscount({ ...newDiscount, product_id: e.target.value })}
                          className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-[11px] outline-none cursor-pointer"
                          style={darkSelectStyle}
                        >
                          <option value="" style={darkOptionStyle}>Produkt wählen</option>
                          {ADMIN_PRODUCTS.filter(p => p.id).map(p => (
                            <option key={p.id} value={p.id} style={darkOptionStyle}>{p.name}</option>
                          ))}
                        </select>
                      )}
                      <input
                        type="number"
                        value={newDiscount.max_uses}
                        onChange={e => setNewDiscount({ ...newDiscount, max_uses: e.target.value })}
                        placeholder="Max. Nutzungen (leer = unbegrenzt)"
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-[11px] outline-none focus:border-white/30"
                      />
                      <input
                        type="date"
                        value={newDiscount.expires_at}
                        onChange={e => setNewDiscount({ ...newDiscount, expires_at: e.target.value })}
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-[11px] outline-none focus:border-white/30"
                        style={darkSelectStyle}
                      />
                      <button type="submit" className="monolith-heavy px-4 py-2 flex items-center justify-center gap-2 text-white/60 hover:text-white hover:bg-white hover:text-black transition-all"
                      >
                        <Plus size={14} />
                        <span className="font-mono text-[9px] uppercase">Erstellen</span>
                      </button>
                    </div>
                  </form>

                  <AdminListView
                    storageKey="discounts"
                    data={discountCodes.filter(c =>
                      discountActiveFilter === 'all' ? true
                      : discountActiveFilter === 'active' ? !!c.is_active
                      : !c.is_active
                    )}
                    rowKey={(c) => c.id || c._id || c.code}
                    searchValue={discountSearch}
                    onSearchChange={setDiscountSearch}
                    searchPlaceholder="Code suchen..."
                    searchFields={['code', 'product_id']}
                    emptyText="Noch keine Rabattcodes"
                    emptyIcon={Percent}
                    filters={[{
                      id: 'active', label: 'Status',
                      value: discountActiveFilter, onChange: setDiscountActiveFilter,
                      options: [
                        { value: 'all', label: 'Alle' },
                        { value: 'active', label: 'Aktiv' },
                        { value: 'inactive', label: 'Inaktiv' },
                      ],
                    }]}
                    columns={[
                      { id: 'code', label: 'Code', locked: true, render: (c) => (
                          <div className="flex items-center gap-2">
                            <code className="font-mono text-sm text-white bg-white/5 px-2 py-0.5 rounded">{c.code}</code>
                            <button
                              onClick={(e) => { e.stopPropagation(); copyAdminToClipboard(c.code, c._id || c.id); }}
                              className="text-white/30 hover:text-white"
                              title="Code kopieren"
                            >
                              {adminCopiedCode === (c._id || c.id) ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                            </button>
                          </div>
                        ) },
                      { id: 'discount_percent', label: 'Rabatt', render: (c) => <span className="font-mono">{c.discount_percent}%</span> },
                      { id: 'scope', label: 'Bereich', render: (c) => (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase ${
                            c.scope === 'cart_total' ? 'bg-blue-500/20 text-blue-300' :
                            c.scope === 'specific_product' ? 'bg-purple-500/20 text-purple-300' :
                            'bg-white/10 text-white/50'
                          }`}>
                            {c.scope === 'cart_total' ? 'Warenkorb' : c.scope === 'specific_product' ? (c.product_id || 'Produkt') : 'Alle'}
                          </span>
                        ) },
                      { id: 'usage', label: 'Nutzungen', mono: true, render: (c) => `${c.uses_count || 0}${c.max_uses ? `/${c.max_uses}` : ''}` },
                      { id: 'expires_at', label: 'Läuft ab', mono: true, defaultVisible: false, render: (c) => c.expires_at ? new Date(c.expires_at).toLocaleDateString('de-DE') : '∞' },
                      { id: 'status', label: 'Status', render: (c) => (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleDiscountCode(c.id); }}
                            className={`px-3 py-1 rounded-lg font-mono text-[9px] uppercase transition-all ${
                              c.is_active ? 'bg-green-500/20 text-green-300 hover:bg-green-500/30' : 'bg-white/5 text-white/30 hover:bg-white/10'
                            }`}
                            title="Aktiv/Inaktiv schalten"
                          >
                            {c.is_active ? 'Aktiv' : 'Inaktiv'}
                          </button>
                        ) },
                    ]}
                    rowActions={(c) => (
                      <button onClick={() => deleteDiscountCode(c.id)} className="p-1.5 text-white/20 hover:text-red-400">
                        <Trash2 size={12} />
                      </button>
                    )}
                  />
                </div>
              )}

              {/* Licenses Tab */}
              {adminActiveTab === 'gifts' && (
                <div className="space-y-6">
                  {/* Verschenken — ein Produkt kostenlos an eine beliebige
                      E-Mail geben. Es entsteht ein echter Lizenzschluessel samt
                      Beleg, die Empfaengerin sieht ihn in ihrer Bibliothek und
                      das Plugin aktiviert genau wie bei einem Kauf.

                      Stand frueher unten im Lizenzen-Reiter, hinter der
                      Schluesselliste — dort hat es niemand gefunden. Es ist
                      eine eigene Handlung, also ein eigener Reiter. */}
                  <div className="monolith-glass p-5 border border-orange-500/30">
                    <div className="flex items-center gap-2 mb-4">
                      <Gift size={14} className="text-orange-300" />
                      <h3 className="font-mono text-xs uppercase tracking-widest text-orange-300">
                        Produkt freischalten (gratis)
                      </h3>
                    </div>
                    <p className="text-white/50 text-xs mb-4 leading-relaxed">
                      Für Tester, Partner, Kollegen oder Reviewer — der/die Empfänger:in bekommt
                      einen echten Lizenzschlüssel per Mail und sieht das Produkt sofort im
                      Kundenportal. Wird auch ohne vorherigen Kauf erstellt; die E-Mail muss
                      sich nicht vorher angemeldet haben.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {/* Email picker: native datalist of existing account
                          emails so the admin picks the EXACT account email
                          (no typos, no case mismatch — which is why grants
                          weren't showing up). Free-text still allowed for
                          testers who haven't registered yet. */}
                      <input
                        type="email"
                        list="aether-grant-user-emails"
                        value={grantForm.email}
                        onChange={e => setGrantForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="Nutzer wählen oder E-Mail eingeben"
                        autoComplete="off"
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 font-mono text-sm text-white placeholder:text-white/20 outline-none focus:border-orange-500/40 md:col-span-2"
                        data-testid="aether-grant-email"
                      />
                      <datalist id="aether-grant-user-emails">
                        {grantUsers.map(u => (
                          <option key={u.id || u.email} value={u.email}>
                            {u.email}{u.role ? ` · ${u.role}` : ''}
                          </option>
                        ))}
                      </datalist>
                      <select
                        value={grantForm.product_id}
                        onChange={e => setGrantForm(prev => ({ ...prev, product_id: e.target.value }))}
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 font-mono text-sm text-white outline-none focus:border-orange-500/40 cursor-pointer"
                        style={darkSelectStyle}
                        data-testid="aether-grant-product"
                      >
                        {/* Suite + the live paid extensions (PLUGIN_PRODUCTS).
                            Free items (project files, presets, SFX Grid) are
                            already downloadable by anyone, so granting is moot;
                            Effect Flow isn't live yet. */}
                        <option value="all" style={darkOptionStyle}>★ Suite (alle Extensions)</option>
                        {/* Instant Curve (frueher Instant Curve) kostet wie jedes Modul
                            und steht deshalb in der Verschenk-Liste des Servers. */}
                        {PLUGIN_PRODUCTS.map(p => (
                          <option key={p.id} value={p.id} style={darkOptionStyle}>{p.name}</option>
                        ))}
                      </select>
                      <select
                        value={grantForm.license_type}
                        onChange={e => setGrantForm(prev => ({ ...prev, license_type: e.target.value }))}
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 font-mono text-sm text-white outline-none focus:border-orange-500/40 cursor-pointer"
                        style={darkSelectStyle}
                        data-testid="aether-grant-type"
                      >
                        <option value="single" style={darkOptionStyle}>Single (1 PC)</option>
                        <option value="team" style={darkOptionStyle}>Team (5 PCs)</option>
                      </select>
                      {/* Wie lange das Geschenk gilt. Ein Jahr ist der Normalfall:
                          verlaengern kann man jederzeit, ein verschenktes
                          Fuer-immer bekommt man nicht mehr zurueck. */}
                      <select
                        value={grantForm.gueltig_tage}
                        onChange={e => setGrantForm(prev => ({ ...prev, gueltig_tage: e.target.value }))}
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 font-mono text-sm text-white outline-none focus:border-orange-500/40 cursor-pointer"
                        style={darkSelectStyle}
                        data-testid="aether-grant-laufzeit"
                      >
                        <option value="30" style={darkOptionStyle}>30 Tage</option>
                        <option value="90" style={darkOptionStyle}>3 Monate</option>
                        <option value="365" style={darkOptionStyle}>1 Jahr</option>
                        <option value="730" style={darkOptionStyle}>2 Jahre</option>
                        <option value="0" style={darkOptionStyle}>Unbefristet</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                      <input
                        value={grantForm.note}
                        onChange={e => setGrantForm(prev => ({ ...prev, note: e.target.value }))}
                        placeholder="Notiz (optional, z. B. 'Tester Markus')"
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 font-mono text-xs text-white placeholder:text-white/20 outline-none focus:border-orange-500/40 md:col-span-3"
                      />
                      <button
                        onClick={handleAdminGrant}
                        disabled={granting || !grantForm.email.trim()}
                        className="bg-orange-500 hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed transition text-black font-bold px-4 py-2.5 rounded-lg font-mono text-[10px] uppercase tracking-widest flex items-center justify-center gap-2"
                        data-testid="aether-grant-submit"
                      >
                        <Gift size={14} />
                        {granting ? 'Erstelle …' : 'Freischalten'}
                      </button>
                    </div>

                    {grantResult && (
                      <div
                        className={`mt-4 flex items-start gap-3 p-3 rounded-lg border ${
                          grantResult.ok
                            ? 'border-green-500/30 bg-green-500/5'
                            : 'border-red-500/30 bg-red-500/5'
                        }`}
                        data-testid="aether-grant-result"
                      >
                        {grantResult.ok ? (
                          <CheckCircle2 size={16} className="text-green-400 shrink-0 mt-0.5" />
                        ) : (
                          <AlertCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
                        )}
                        <div className="text-xs">
                          {grantResult.ok ? (
                            <div className="text-green-300 space-y-1">
                              <p>
                                Freigeschaltet — {grantResult.email} hat Zugriff auf{' '}
                                <strong>{grantResult.product}</strong> (
                                {grantResult.license_type}){grantResult.gueltig_bis
                                  ? <> bis <strong>{new Date(grantResult.gueltig_bis).toLocaleDateString('de-DE')}</strong></>
                                  : <> <strong>unbefristet</strong></>}. Lizenzschlüssel:{' '}
                                <code className="font-mono text-white">{grantResult.license_key}</code>.
                              </p>
                              {grantResult.email_sent === false ? (
                                <p className="text-orange-300">
                                  ⚠ Lizenz erstellt, aber die E-Mail wurde NICHT versendet.
                                  Prüfe die Mail-Konfiguration (Resend-Key / Absender) auf dem Server.
                                </p>
                              ) : (
                                <p className="text-green-400/80">✓ Bestätigungs-Mail ist raus.</p>
                              )}
                              {/* Round-trip confirmation. library_count is how
                                  many products this email now resolves to in
                                  the customer portal. If it's 0 right after a
                                  grant, the email doesn't match an account /
                                  is mistyped — flag it loudly. */}
                              {typeof grantResult.library_count === 'number' && (
                                grantResult.library_count > 0 ? (
                                  <p className="text-green-400/80">
                                    ✓ Im Kundenportal sichtbar: {grantResult.library_count} Produkt
                                    {grantResult.library_count === 1 ? '' : 'e'} unter dieser E-Mail.
                                  </p>
                                ) : (
                                  <p className="text-orange-300">
                                    ⚠ Achtung: Diese E-Mail hat aktuell 0 Produkte im Portal —
                                    prüfe, ob sie exakt der Account-E-Mail entspricht.
                                  </p>
                                )
                              )}
                            </div>
                          ) : (
                            <p className="text-red-300">Fehler: {grantResult.error}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {adminActiveTab === 'licenses' && (
                <div className="space-y-6">
                  {/* License Stats */}
                  {licenseStats && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <div className="monolith-glass p-4">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">Gesamt</div>
                        <div className="font-mono text-2xl text-white">{licenseStats.total_keys}</div>
                      </div>
                      <div className="monolith-glass p-4">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">Aktiv</div>
                        <div className="font-mono text-2xl text-green-400">{licenseStats.active_keys}</div>
                      </div>
                      <div className="monolith-glass p-4">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">Aktivierungen</div>
                        <div className="font-mono text-2xl text-blue-400">{licenseStats.total_activations}</div>
                      </div>
                      <div className="monolith-glass p-4">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">Team-Keys</div>
                        <div className="font-mono text-2xl text-purple-400">{licenseStats.by_type?.team || 0}</div>
                      </div>
                    </div>
                  )}

                  {/* Create New License */}
                  <div className="monolith-glass p-5 border border-white/10">
                    <h3 className="font-mono text-xs uppercase tracking-widest text-white/40 mb-4">Neue Lizenz erstellen</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <select
                        value={newLicense.key_type}
                        onChange={e => setNewLicense(prev => ({ ...prev, key_type: e.target.value }))}
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 font-mono text-sm text-white outline-none focus:border-white/30 cursor-pointer"
                        style={darkSelectStyle}
                      >
                        <option value="single" style={darkOptionStyle}>Single (1 PC)</option>
                        <option value="team" style={darkOptionStyle}>Team (5 PCs)</option>
                        <option value="unlimited" style={darkOptionStyle}>Unlimited</option>
                      </select>
                      <select
                        value={newLicense.products}
                        onChange={e => setNewLicense(prev => ({ ...prev, products: e.target.value }))}
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 font-mono text-sm text-white outline-none focus:border-white/30 cursor-pointer"
                        style={darkSelectStyle}
                      >
                        {licenseProducts.map(p => (
                          <option key={p.key} value={p.key} style={darkOptionStyle}>{p.name}</option>
                        ))}
                      </select>
                      <input
                        value={newLicense.note}
                        onChange={e => setNewLicense(prev => ({ ...prev, note: e.target.value }))}
                        placeholder="Notiz (optional)"
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 font-mono text-sm text-white placeholder:text-white/20 outline-none focus:border-white/30"
                      />
                      <button
                        onClick={createLicense}
                        className="monolith-glass px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest hover:bg-white hover:text-black transition-all flex items-center justify-center gap-2"
                        data-testid="aether-create-license-btn"
                      >
                        <Plus size={14} />
                        Erstellen
                      </button>
                    </div>

                    {/* Trial / expiry row — second strip under the main fields.
                        Leave empty for a perpetual key (every paid sale).
                        Quick chips for the common trial lengths so Dave can
                        spin up a 7-day key without typing. */}
                    <div className="mt-4 pt-4 border-t border-white/5 flex flex-wrap items-center gap-3">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                        Test-Lizenz mit Ablauf:
                      </span>
                      {[
                        { label: '7 Tage', value: 7 },
                        { label: '14 Tage', value: 14 },
                        { label: '30 Tage', value: 30 },
                      ].map(({ label, value }) => {
                        const active = newLicense.expires_in_days === value;
                        return (
                          <button
                            key={value}
                            onClick={() =>
                              setNewLicense(prev => ({
                                ...prev,
                                expires_in_days: prev.expires_in_days === value ? null : value,
                              }))
                            }
                            className={`px-3 py-1.5 rounded-md font-mono text-[10px] uppercase tracking-widest transition-all ${
                              active
                                ? 'bg-orange-500/20 border border-orange-500/40 text-orange-300'
                                : 'bg-white/5 border border-white/10 text-white/50 hover:text-white hover:bg-white/10'
                            }`}
                            data-testid={`aether-trial-${value}`}
                          >
                            {label}
                          </button>
                        );
                      })}
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={
                          newLicense.expires_in_days &&
                          ![7, 14, 30].includes(newLicense.expires_in_days)
                            ? newLicense.expires_in_days
                            : ''
                        }
                        onChange={e => {
                          const v = parseInt(e.target.value, 10);
                          setNewLicense(prev => ({
                            ...prev,
                            expires_in_days: Number.isFinite(v) && v > 0 ? v : null,
                          }));
                        }}
                        placeholder="Tage (custom)"
                        className="w-32 bg-[#0a0a0a] border border-white/10 rounded-md px-2 py-1.5 font-mono text-[10px] text-white placeholder:text-white/20 outline-none focus:border-white/30"
                        data-testid="aether-trial-custom"
                      />
                      {newLicense.expires_in_days && (
                        <button
                          onClick={() => setNewLicense(prev => ({ ...prev, expires_in_days: null }))}
                          className="px-2 py-1.5 rounded-md font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white"
                        >
                          ×&nbsp;keine
                        </button>
                      )}
                      {!newLicense.expires_in_days && (
                        <span className="font-mono text-[10px] text-white/30">
                          (leer = unbegrenzt — normaler Verkauf)
                        </span>
                      )}
                    </div>
                  </div>


                  {/* Search and Filters */}
                  <div className="flex flex-wrap gap-3">
                    <div className="monolith-glass flex items-center px-3 py-2 flex-1 min-w-[200px]">
                      <Search size={14} className="text-white/30 mr-2" />
                      <input
                        value={licenseSearch}
                        onChange={e => setLicenseSearch(e.target.value)}
                        placeholder="Suchen..."
                        className="bg-transparent border-none outline-none font-mono text-[10px] w-full text-white placeholder:text-white/20"
                      />
                    </div>
                    <select
                      value={licenseProductFilter}
                      onChange={e => setLicenseProductFilter(e.target.value)}
                      className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 font-mono text-[10px] text-white outline-none cursor-pointer"
                      style={darkSelectStyle}
                    >
                      <option value="all" style={darkOptionStyle}>Alle Produkte</option>
                      {licenseProducts.map(p => (
                        <option key={p.key} value={p.key} style={darkOptionStyle}>{p.name}</option>
                      ))}
                    </select>
                    <select
                      value={licenseStatusFilter}
                      onChange={e => setLicenseStatusFilter(e.target.value)}
                      className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 font-mono text-[10px] text-white outline-none cursor-pointer"
                      style={darkSelectStyle}
                    >
                      <option value="all" style={darkOptionStyle}>Alle Status</option>
                      <option value="active" style={darkOptionStyle}>Aktiv</option>
                      <option value="inactive" style={darkOptionStyle}>Inaktiv</option>
                    </select>
                    <select
                      value={licenseTypeFilter}
                      onChange={e => setLicenseTypeFilter(e.target.value)}
                      className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 font-mono text-[10px] text-white outline-none cursor-pointer"
                      style={darkSelectStyle}
                    >
                      <option value="all" style={darkOptionStyle}>Alle Typen</option>
                      <option value="single" style={darkOptionStyle}>Single</option>
                      <option value="team" style={darkOptionStyle}>Team</option>
                      <option value="unlimited" style={darkOptionStyle}>Unlimited</option>
                    </select>
                  </div>

                  {/* Licenses List */}
                  <div className="space-y-4">
                    {licenseLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 size={24} className="animate-spin text-white/30" />
                      </div>
                    ) : licenses.length === 0 ? (
                      <div className="text-center py-12 text-white/30 font-mono text-sm">
                        Keine Lizenzen gefunden
                      </div>
                    ) : (
                      licenses.map(license => (
                        <div 
                          key={license.id} 
                          className={`monolith-glass p-4 flex flex-wrap items-center justify-between gap-3 aether-card-hover cursor-pointer ${!license.is_active ? 'opacity-50' : ''}`}
                          onClick={() => viewLicenseDetail(license.id)}
                        >
                          <div className="flex-1 min-w-[200px]">
                            <div className="flex items-center gap-2 flex-wrap">
                              <code className="font-mono text-sm text-white bg-white/5 px-2 py-0.5 rounded">{license.key}</code>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(license.key);
                                  setAdminCopiedCode(license.id);
                                  setTimeout(() => setAdminCopiedCode(null), 2000);
                                }}
                                className="text-white/30 hover:text-white"
                              >
                                {adminCopiedCode === license.id ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                              </button>
                            </div>
                            <div className="flex items-center gap-3 mt-2 flex-wrap">
                              <span className={`px-2 py-0.5 rounded font-mono text-[8px] uppercase ${
                                license.key_type === 'single' ? 'bg-blue-500/20 text-blue-300' :
                                license.key_type === 'team' ? 'bg-purple-500/20 text-purple-300' :
                                'bg-orange-500/20 text-orange-300'
                              }`}>
                                {license.key_type}
                              </span>
                              <span className="font-mono text-[9px] text-white/40">
                                {license.current_activations}/{license.max_activations} aktiv
                              </span>
                              <span className="px-2 py-0.5 rounded font-mono text-[8px] bg-white/5 text-white/50">
                                {licenseProducts.find(p => p.key === license.products)?.name || license.products}
                              </span>
                              {license.note && (
                                <span className="font-mono text-[9px] text-white/30 truncate max-w-[150px]">{license.note}</span>
                              )}
                              {license.expires_at && (() => {
                                // Trial / time-limited keys: show how many
                                // days remain (or 'abgelaufen' when past).
                                const exp = new Date(license.expires_at);
                                const ms = exp.getTime() - Date.now();
                                const days = Math.ceil(ms / 86400000);
                                const expired = ms <= 0;
                                return (
                                  <span
                                    className={`px-2 py-0.5 rounded font-mono text-[8px] uppercase ${
                                      expired
                                        ? 'bg-red-500/20 text-red-300'
                                        : 'bg-orange-500/20 text-orange-300'
                                    }`}
                                    title={`Ablauf: ${exp.toLocaleString('de-DE')}`}
                                  >
                                    {expired ? 'abgelaufen' : `Trial · ${days}d`}
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleLicense(license.id); }}
                              className={`px-3 py-1.5 rounded-lg font-mono text-[9px] uppercase transition-all ${
                                license.is_active
                                  ? 'bg-green-500/20 text-green-300 hover:bg-green-500/30'
                                  : 'bg-white/5 text-white/30 hover:bg-white/10'
                              }`}
                            >
                              {license.is_active ? 'Aktiv' : 'Inaktiv'}
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteLicense(license.id); }}
                              className="p-2 text-white/20 hover:text-red-400 transition-colors"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Admin Users Tab */}
              {adminActiveTab === 'users' && (
                <AetherUserManagement />
              )}

              {/* Admin Invoices Tab — free-form invoices (video jobs etc.) */}
              {adminActiveTab === 'invoices' && (
                <InvoiceBuilder />
              )}

              {/* ===================== DSGVO TAB ===================== */}
              {adminActiveTab === 'gdpr' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="font-mono text-xs uppercase tracking-widest text-white/40 mb-1">DSGVO-Werkzeuge</h3>
                    <p className="text-white/60 text-sm">Auskunfts- und Löschanfragen pro E-Mail-Adresse, plus die Retention-Sweeps die in der Datenschutzerklärung versprochen sind.</p>
                  </div>

                  {/* Export / Erase by email */}
                  <div className="monolith-glass p-5">
                    <h4 className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">Pro E-Mail-Adresse</h4>
                    <div className="flex flex-wrap gap-3 mb-4">
                      <input
                        type="email"
                        value={gdprEmail}
                        onChange={(e) => setGdprEmail(e.target.value)}
                        placeholder="kunde@example.com"
                        className="flex-1 min-w-[220px] bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-[11px] outline-none focus:border-white/30"
                      />
                      <button
                        onClick={async () => {
                          if (!gdprEmail.trim()) return;
                          setGdprBusy(true);
                          try {
                            const res = await authFetch(`/api/admin/gdpr/export?email=${encodeURIComponent(gdprEmail.trim())}`);
                            const data = await res.json();
                            if (!res.ok) throw new Error(data.detail || 'Export fehlgeschlagen');
                            // Trigger JSON download
                            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `gdpr_export_${gdprEmail.trim().replace(/[^a-z0-9]/gi, '_')}.json`;
                            document.body.appendChild(a); a.click(); document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                            setGdprResult({ kind: 'export', ...data.counts });
                          } catch (e) {
                            setGdprResult({ error: e.message });
                          } finally {
                            setGdprBusy(false);
                          }
                        }}
                        disabled={gdprBusy || !gdprEmail.trim()}
                        className="monolith-heavy px-4 py-2 flex items-center gap-2 text-white/60 hover:text-white text-[9px] font-mono uppercase tracking-widest disabled:opacity-40"
                      >
                        <DownloadIcon size={12} />
                        Art. 15 Auskunft (JSON)
                      </button>
                      <button
                        onClick={async () => {
                          if (!gdprEmail.trim()) return;
                          if (!window.confirm(
                            `Daten zu „${gdprEmail.trim()}" gemäß DSGVO Art. 17 löschen?\n\n`
                            + 'Bestellungen werden pseudonymisiert (Tax-Aufbewahrung 10 Jahre), '
                            + 'E-Mail-Log, Archiv, Affiliate-Einträge und Lizenz-Aktivierungen werden hart gelöscht. '
                            + 'Lizenzschlüssel werden deaktiviert.\n\nDiese Aktion ist nicht rückgängig zu machen.'
                          )) return;
                          setGdprBusy(true);
                          try {
                            const res = await authFetch(`/api/admin/gdpr/erase?email=${encodeURIComponent(gdprEmail.trim())}`, { method: 'DELETE' });
                            const data = await res.json();
                            if (!res.ok) throw new Error(data.detail || 'Erase fehlgeschlagen');
                            setGdprResult({ kind: 'erase', ...data });
                            fetchAdminData();
                          } catch (e) {
                            setGdprResult({ error: e.message });
                          } finally {
                            setGdprBusy(false);
                          }
                        }}
                        disabled={gdprBusy || !gdprEmail.trim()}
                        className="px-4 py-2 flex items-center gap-2 bg-red-500/15 text-red-300 hover:bg-red-500/25 rounded-lg text-[9px] font-mono uppercase tracking-widest disabled:opacity-40"
                      >
                        <Trash2 size={12} />
                        Art. 17 Löschung
                      </button>
                    </div>
                    {gdprResult && (
                      <div className={`text-[11px] font-mono p-3 rounded-lg ${gdprResult.error ? 'bg-red-500/10 text-red-300' : 'bg-emerald-500/10 text-emerald-300'}`}>
                        {gdprResult.error
                          ? `Fehler: ${gdprResult.error}`
                          : gdprResult.kind === 'export'
                            ? `Auskunft erstellt: ${gdprResult.orders} Bestellungen · ${gdprResult.email_log} E-Mails · ${gdprResult.licenses} Lizenzen · ${gdprResult.activations} Aktivierungen. JSON wurde heruntergeladen.`
                            : `Löschung erfolgt: ${gdprResult.orders_pseudonymised} Bestellungen pseudonymisiert · ${gdprResult.email_log_deleted} E-Mails gelöscht · ${gdprResult.affiliates_deleted} Affiliates · ${gdprResult.licenses_deactivated} Lizenzen deaktiviert · ${gdprResult.activations_deleted} Aktivierungen entfernt.`}
                      </div>
                    )}
                  </div>

                  {/* DB Backup download */}
                  <div className="monolith-glass p-5">
                    <h4 className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">Backup herunterladen</h4>
                    <p className="text-white/50 text-xs mb-3">
                      ZIP mit allen Daten: Bestellungen, E-Mails, Affiliates, Lizenzen, Activity-Log, Archive.
                      Off-site Snapshot für den Fall dass Railway mal eine ungemütliche Stunde hat.
                    </p>
                    <button
                      onClick={async () => {
                        try {
                          const res = await authFetch('/api/admin/backup/download');
                          if (!res.ok) throw new Error(`HTTP ${res.status}`);
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          const ts = new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', '-');
                          a.download = `davidmedia_backup_${ts}.zip`;
                          document.body.appendChild(a); a.click(); document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        } catch (e) {
                          alert('Backup fehlgeschlagen: ' + e.message);
                        }
                      }}
                      className="px-4 py-2 rounded-lg text-[9px] font-mono uppercase tracking-widest text-white liquid-glass hover:bg-white/10 flex items-center gap-2"
                    >
                      <DownloadIcon size={12} />
                      Vollständiges Backup ziehen
                    </button>
                  </div>

                  {/* Retention sweep */}
                  <div className="monolith-glass p-5">
                    <h4 className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">Retention-Sweep (E-Mail-Log)</h4>
                    <p className="text-white/50 text-xs mb-3">
                      Die Datenschutzerklärung verspricht: E-Mail-Logs werden nach 12 Monaten automatisch gelöscht.
                      Activity-Log bleibt 24 Monate (Audit-Trail).
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="text-white/60 text-[11px] font-mono">Älter als
                        <input
                          type="number" min={3} max={120}
                          value={retentionMonths}
                          onChange={(e) => setRetentionMonths(Math.max(3, parseInt(e.target.value) || 12))}
                          className="ml-2 w-16 bg-[#0a0a0a] border border-white/10 rounded px-2 py-1 text-white text-[11px] outline-none"
                        /> Monate
                      </label>
                      <button
                        onClick={async () => {
                          setGdprBusy(true);
                          try {
                            const res = await authFetch(`/api/admin/gdpr/retention-preview?months=${retentionMonths}`);
                            const data = await res.json();
                            if (!res.ok) throw new Error(data.detail || 'Vorschau fehlgeschlagen');
                            setRetentionPreview(data);
                          } catch (e) {
                            setRetentionPreview({ error: e.message });
                          } finally {
                            setGdprBusy(false);
                          }
                        }}
                        disabled={gdprBusy}
                        className="px-3 py-2 rounded-lg text-[9px] font-mono uppercase tracking-widest text-white/70 hover:text-white liquid-glass disabled:opacity-40"
                      >
                        Vorschau zählen
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Wirklich alle E-Mail-Log-Einträge älter als ${retentionMonths} Monate löschen?`)) return;
                          setGdprBusy(true);
                          try {
                            const res = await authFetch(`/api/admin/gdpr/retention-sweep?months=${retentionMonths}`, { method: 'POST' });
                            const data = await res.json();
                            if (!res.ok) throw new Error(data.detail || 'Sweep fehlgeschlagen');
                            setRetentionPreview({ done: true, ...data });
                            fetchAdminData();
                          } catch (e) {
                            setRetentionPreview({ error: e.message });
                          } finally {
                            setGdprBusy(false);
                          }
                        }}
                        disabled={gdprBusy}
                        className="px-3 py-2 rounded-lg text-[9px] font-mono uppercase tracking-widest bg-orange-500/15 text-orange-300 hover:bg-orange-500/25 disabled:opacity-40"
                      >
                        Sweep ausführen
                      </button>
                    </div>
                    {retentionPreview && (
                      <div className={`mt-3 text-[11px] font-mono p-3 rounded-lg ${retentionPreview.error ? 'bg-red-500/10 text-red-300' : retentionPreview.done ? 'bg-emerald-500/10 text-emerald-300' : 'bg-white/5 text-white/70'}`}>
                        {retentionPreview.error
                          ? `Fehler: ${retentionPreview.error}`
                          : retentionPreview.done
                            ? `Erledigt: ${retentionPreview.email_log_deleted} E-Mail-Logs gelöscht · ${retentionPreview.activity_log_deleted} Activity-Einträge gelöscht.`
                            : `Würde löschen: ${retentionPreview.email_log_to_delete} E-Mail-Logs · ${retentionPreview.activity_log_to_delete} Activity-Einträge (älter als 24 Monate).`}
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>{/* /tab content */}
            </div>
            );
          })()}
        </div>
      </main>

      {/* ========== BROADCAST EMAIL MODAL ========== */}
      {broadcastOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) setBroadcastOpen(false); }}>
          <div className="monolith-glass max-w-2xl w-full p-6 rounded-2xl" style={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Send size={18} className="text-orange-400" />
                <h2 className="font-mono text-sm uppercase tracking-widest text-white">E-Mail-Broadcast</h2>
              </div>
              <button onClick={() => setBroadcastOpen(false)} className="p-2 text-white/40 hover:text-white"><X size={16} /></button>
            </div>
            <p className="text-white/40 text-xs font-mono mb-4">An alle Kunden einer Zielgruppe senden. Tipp: <strong>Vorschau</strong> verrät dir die Empfängeranzahl, bevor du wirklich versendest.</p>

            <label className="block text-[9px] font-mono uppercase tracking-widest text-white/40 mb-1">Zielgruppe</label>
            <select
              value={broadcastForm.audience}
              onChange={(e) => setBroadcastForm({ ...broadcastForm, audience: e.target.value })}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 mb-3 text-white font-mono text-[11px] outline-none cursor-pointer"
              style={darkSelectStyle}
            >
              <option value="all_paid" style={darkOptionStyle}>Alle zahlenden Kunden</option>
              <option value="all_known" style={darkOptionStyle}>Alle bekannten E-Mails (inkl. Free)</option>
              {PLUGIN_PRODUCTS.map(p => (
                <option key={p.id} value={p.id} style={darkOptionStyle}>Nur {p.name}-Käufer</option>
              ))}
            </select>

            <label className="block text-[9px] font-mono uppercase tracking-widest text-white/40 mb-1">Betreff</label>
            <input
              type="text"
              value={broadcastForm.subject}
              onChange={(e) => setBroadcastForm({ ...broadcastForm, subject: e.target.value })}
              placeholder="z.B. Update: Pre-Release Status"
              className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 mb-3 text-white font-mono text-[11px] outline-none focus:border-white/30"
            />

            <label className="block text-[9px] font-mono uppercase tracking-widest text-white/40 mb-1">
              Inhalt (HTML erlaubt — z.B. &lt;p&gt;, &lt;strong&gt;, &lt;a href=…&gt;)
            </label>
            <textarea
              value={broadcastForm.body_html}
              onChange={(e) => setBroadcastForm({ ...broadcastForm, body_html: e.target.value })}
              placeholder="<p>Hallo!</p><p>Kurzes Update zum Plugin: …</p>"
              rows={8}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 mb-4 text-white font-mono text-[11px] outline-none focus:border-white/30 resize-none"
            />

            {broadcastResult && (
              <div className={`p-3 rounded-lg mb-4 text-xs font-mono ${broadcastResult.error ? 'bg-red-500/10 text-red-300' : 'bg-green-500/10 text-green-300'}`}>
                {broadcastResult.error
                  ? `Fehler: ${broadcastResult.error}`
                  : broadcastResult.dry_run
                    ? `Vorschau: ${broadcastResult.recipient_count} Empfänger${broadcastResult.sample?.length ? ` — z.B. ${broadcastResult.sample.slice(0, 5).join(', ')}` : ''}`
                    : `Versendet: ${broadcastResult.sent}/${broadcastResult.recipient_count}${broadcastResult.failed ? ` (${broadcastResult.failed} Fehler)` : ''}`}
              </div>
            )}

            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => sendBroadcastEmail({ dryRun: true })}
                disabled={broadcastBusy}
                className="px-4 py-2 rounded-lg text-xs font-mono uppercase tracking-widest text-white/70 hover:text-white liquid-glass disabled:opacity-40"
              >
                {broadcastBusy ? <Loader2 size={14} className="animate-spin inline mr-2" /> : null}
                Vorschau (zählen)
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => setBroadcastOpen(false)}
                  className="px-4 py-2 rounded-lg text-xs font-mono uppercase tracking-widest text-white/40 hover:text-white"
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => {
                    if (!window.confirm(`Wirklich an alle Empfänger der Zielgruppe „${broadcastForm.audience}" senden?`)) return;
                    sendBroadcastEmail({ dryRun: false });
                  }}
                  disabled={broadcastBusy || !broadcastForm.subject.trim() || !broadcastForm.body_html.trim()}
                  className="px-4 py-2 rounded-lg text-xs font-mono uppercase tracking-widest bg-orange-500/20 text-orange-300 hover:bg-orange-500/30 disabled:opacity-40 flex items-center gap-2"
                >
                  {broadcastBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Senden
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right sidebar resize handle + Task Feed — admin-only productivity
          rail. Customers/partners don't get a notes/todo sidebar, so the
          main content simply widens to fill the space. */}
      {isAdmin && (<>
      <div
        onMouseDown={startSidebarResize('right')}
        className="hidden lg:block w-1.5 cursor-col-resize group relative shrink-0 z-30"
        style={{ height: '100%' }}
        title="Ziehen zum Vergrößern"
      >
        <div className="absolute inset-0 group-hover:bg-orange-500/40 group-active:bg-orange-500/60 transition-colors" />
      </div>

      {/* --- RIGHT SIDEBAR: TASK FEED ---
          Width driven by --aether-right-width on desktop. */}
      <aside
        style={{ '--aether-right-width': `${rightSidebarWidth}px` }}
        className={`
          ${showMobileVault ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
          mobile-drawer right-0 flex flex-col border-l border-white/10 bg-transparent overflow-y-auto aether-scrollbar p-6 lg:p-8
          relative
        `}
      >
        <div className="task-feed-header flex justify-between items-center mb-6 shrink-0 gap-2">
          <div className="flex items-center gap-3 text-white/40 min-w-0" title="Task Feed">
            <ListTodo size={18} className="shrink-0" />
            <span className="task-feed-label font-mono text-[10px] uppercase tracking-widest truncate">Task Feed</span>
          </div>
          <div className="flex gap-2 items-center shrink-0">
            <span
              className="font-mono text-[9px] text-white/30 px-2 py-1 bg-white/5 rounded-full whitespace-nowrap"
              title={`${tasks.filter(t => !t.completed).length} offene Tasks`}
            >
              {tasks.filter(t => !t.completed).length}
              <span className="task-feed-suffix"> offen</span>
            </span>
            <button onClick={() => setShowMobileVault(false)} className="lg:hidden p-2 monolith-glass">
              <X size={16}/>
            </button>
          </div>
        </div>

        {/* Task List */}
        <div className="space-y-3 flex-grow overflow-y-auto aether-scrollbar">
          {tasksLoading ? (
            // Skeleton placeholders feel faster than a centred spinner — the
            // eye sees structure instead of "is it stuck?"
            <div className="space-y-3">
              {[0, 1, 2, 3].map((i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ListTodo size={32} className="text-white/10 mb-4" />
              <p className="font-mono text-[10px] text-white/30 uppercase">Keine Tasks</p>
              <p className="font-mono text-[9px] text-white/20 mt-2">Nutze Scriptum um Tasks hinzuzufügen</p>
            </div>
          ) : (
            <>
              {/* Open Tasks */}
              {tasks.filter(t => !t.completed).map(task => (
                <motion.div
                  key={task.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="monolith-glass p-4 group hover:bg-white/[0.03] transition-all"
                >
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => toggleTask(task.id)}
                      className="mt-0.5 w-5 h-5 rounded-full border border-white/20 flex items-center justify-center hover:border-white/40 hover:bg-white/10 transition-all shrink-0"
                    >
                      <Check size={10} className="text-transparent group-hover:text-white/30" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-[11px] text-white/70 leading-relaxed break-words">{task.text}</p>
                      <p className="font-mono text-[9px] text-white/20 mt-2">
                        {new Date(task.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <button
                      onClick={() => deleteTask(task.id)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 text-white/20 hover:text-red-400 transition-all shrink-0"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </motion.div>
              ))}
              
              {/* Completed Tasks */}
              {tasks.filter(t => t.completed).length > 0 && (
                <div className="pt-4 mt-4 border-t border-white/5">
                  <p className="font-mono text-[9px] text-white/20 uppercase mb-3">Erledigt ({tasks.filter(t => t.completed).length})</p>
                  {tasks.filter(t => t.completed).map(task => (
                    <motion.div
                      key={task.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="p-3 group opacity-50 hover:opacity-70 transition-all"
                    >
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => toggleTask(task.id)}
                          className="mt-0.5 w-5 h-5 rounded-full bg-white/10 flex items-center justify-center shrink-0"
                        >
                          <Check size={10} className="text-white/50" />
                        </button>
                        <p className="font-mono text-[10px] text-white/40 line-through flex-1">{task.text}</p>
                        <button
                          onClick={() => deleteTask(task.id)}
                          className="opacity-0 group-hover:opacity-100 p-1.5 text-white/20 hover:text-red-400 transition-all shrink-0"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </aside>
      </>)}

      {/* --- OVERLAY BACKDROP (MOBILE ONLY) --- */}
      {(showMobileSidebar || showMobileVault) && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-[140]" 
          onClick={() => { setShowMobileSidebar(false); setShowMobileVault(false); }} 
        />
      )}
      
      {/* --- SCRIPTUM CONFIRMATION MODAL --- */}
      {showScriptumConfirm && (
        <div 
          className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowScriptumConfirm(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="monolith-heavy p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-mono text-[11px] uppercase tracking-widest text-white mb-4">
              Bestätigung
            </h3>
            <p className="font-mono text-[10px] text-white/60 mb-2">
              Willst du das wirklich posten in:
            </p>
            <div className="monolith-glass p-3 mb-4">
              <p className="font-mono text-[10px] text-white/80">
                {scriptumTarget === 'journal' && '📔 Journal'}
                {scriptumTarget === 'tasks' && '✅ Tasks'}
                {scriptumTarget === 'both' && '📔 Journal + ✅ Tasks'}
              </p>
            </div>
            <div className="p-3 bg-white/5 rounded-lg mb-6 max-h-32 overflow-y-auto aether-scrollbar">
              <p className="font-mono text-[10px] text-white/50 italic">"{pendingNote}"</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowScriptumConfirm(false)}
                className="flex-1 px-4 py-2.5 monolith-glass font-mono text-[9px] uppercase tracking-widest text-white/60 hover:text-white transition-all"
              >
                Abbrechen
              </button>
              <button
                onClick={confirmScriptumPost}
                className="flex-1 px-4 py-2.5 bg-white text-black font-mono text-[9px] uppercase tracking-widest hover:bg-white/90 transition-all rounded-xl"
              >
                Posten
              </button>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* --- IMAGE ZOOM MODAL --- */}
      {zoomImage && (
        <div 
          className="fixed inset-0 z-[300] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setZoomImage(null)}
        >
          <button 
            className="absolute top-6 right-6 p-3 monolith-glass text-white/60 hover:text-white"
            onClick={() => setZoomImage(null)}
          >
            <X size={24} />
          </button>
          <img 
            src={zoomImage} 
            className="max-w-full max-h-full object-contain rounded-2xl" 
            alt="Zoomed" 
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
    </>
  );
}
